import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { bool, func, object, oneOf, shape } from 'prop-types';
import { createResourceLocatorString } from '../../util/routes';
import { isTicketNetworkEventListing } from '../../util/listingEventDate';
import { propTypes } from '../../util/types';
import { createSlug } from '../../util/urlHelpers';
import { hidePageLoadIndicator, showPageLoadIndicator } from '../../util/pageLoadIndicator';
import { patchPreCheckoutCtaInRoot } from '../../util/seaticsBuyTicketsCtaPatch';
import {
  SEATICS_CHECKOUT_QTY_REQUIRED_MSG,
  checkoutQuantityError,
  resolveCheckoutQuantityFromTicketGroup,
  splitBitmapAllowsQty,
  ticketGroupAllowsQuantity,
} from '../../util/seaticsTicketGroupQuantity';
import { applyPromotixSeaticsUiConfig } from '../../util/seaticsUiConfig';
import {
  installSeaticsViewportSizing,
  syncSeaticsViewportHeight,
} from '../../util/seaticsViewportSizing';
import { trackOpenAIAdsCheckoutStarted } from '../../util/openaiAds';
import {
  persistGa4AttributionForTransaction,
  summarizeTicketGroupPrices,
  classifySecondaryLockFailureReason,
  trackSecondaryBeginCheckout,
  trackSecondaryInventoryEmpty,
  trackSecondaryInventoryLoaded,
  trackSecondaryLockFailed,
  trackSecondaryLockSucceeded,
  trackSecondaryTicketSelected,
  trackSecondaryViewItem,
} from '../../util/ga4SecondaryMarketplace';
import { installSeaticsSelectItemTracking } from '../../util/seaticsSelectItemTracking';
import { installSeaticsHashNavigationGuard } from '../../util/seaticsHashNavigationGuard';
import { markSeaticsCheckoutNavigation, withoutHistoryBack } from '../../util/seaticsCheckoutHistory';
import {
  PRECHECKOUT_OPEN_BODY_ATTR,
  NATIVE_WEBVIEW_HTML_ATTR,
  NATIVE_SHEET_BLEED_ATTR,
  installSeaticsPrecheckoutWebViewFix,
  isNativeWebViewMarked,
  isSeaticsPrecheckoutUiOpen,
  markNativeWebViewIfNeeded,
} from '../../util/seaticsPrecheckoutWebViewFix';
import {
  enrichmentParamsFromPublicData,
  fetchListingEnrichment,
  getCachedListingEnrichment,
  setCachedListingEnrichment,
} from '../../util/listingEnrichmentClient';
import { sameOriginApiBase } from '../../util/sameOriginApiBase';
import { resolveEventEntityName } from '../../util/listingSeoCopy';
import { Modal, NamedLink, PrimaryButton, SecondaryButton } from '../../components';
import {
  TN_TRAFFIC_CUSTOMER_MESSAGE,
  isTnUpstreamTrafficError,
  isLockSelectionRetryable,
  lockSelectionRetryDelayMs,
  checkoutAlertMessage as formatCheckoutAlertMessage,
} from '../../util/tnSeaticsCustomerErrors';
import { fetchTnEventAvailability } from '../../util/tnSeaticsAvailabilityFetch';
import { installLowestPricePillPatch, LOWEST_PRICE_PILL_CSS } from '../../util/seaticsLowestPricePill';
import { TICKET_PACKAGES_PROMO_CSS } from '../../util/seaticsTicketPackagesPromo';
import {
  seaticsMapBootstrapUrl,
  seaticsStaticMapUrlFromListing,
} from '../../util/seaticsMapUrls';
import {
  TN_EMPTY_INVENTORY_CUSTOMER_MESSAGE,
  TN_EVENT_DATE_PASSED_CUSTOMER_MESSAGE,
  TN_SEARCH_MORE_EVENTS_CTA_LABEL,
  tnListingUnavailableReason,
  classifyTnAvailabilityOutcome,
} from '../../util/tnSeaticsEmptyInventory';
import { isLikelyTnSeaticsBot } from '../../util/tnSeaticsBotDetect';
import { PROMOTIX_SELECT_TICKETS_ANCHOR_ID } from './promotixSelectTicketsScroll';
import css from './ListingPage.module.css';

/** Map embed availability UI: loading until first classified outcome. */
const MAP_INVENTORY_LOADING = 'loading';
const MAP_INVENTORY_READY = 'ready';
const MAP_INVENTORY_EMPTY = 'empty';
const MAP_INVENTORY_TRAFFIC = 'trafficError';
const MAP_INVENTORY_ERROR = 'error';

/** Debounce sidebar availability refetches when quantity changes (saves TN quota). */
const AVAILABILITY_QUANTITY_DEBOUNCE_MS = 400;

const isTnEventListing = listing => {
  const pd = listing?.attributes?.publicData || {};
  return isTicketNetworkEventListing(pd);
};

const customerIdFromUser = currentUser => currentUser?.id?.uuid || currentUser?.id || null;
const listingIdFromListing = listing => listing?.id?.uuid || listing?.id || null;
const resellerFeePercent = () => {
  const raw = process.env.REACT_APP_SEATICS_RESELLER_FEE_PERCENT;
  const pct = raw == null || raw === '' ? 10 : Number(raw);
  return Number.isFinite(pct) && pct >= 0 ? pct : 10;
};
const roundMoney = n => Math.round(Number(n) * 100) / 100;
const withResellerFee = base => roundMoney(Number(base || 0) * (1 + resellerFeePercent() / 100));
const withoutResellerFee = allIn =>
  roundMoney(Number(allIn || 0) / (1 + resellerFeePercent() / 100));
/**
 * LIST tier as priced by the integration service: TN retail x SEATICS_PRICE_MARKUP_PERCENT.
 * `tgPrice` off the availability feed is already this value — never re-derive it from wholesale,
 * which throws away the retail spread that is our markup margin.
 */
const listUnitPriceFromTicketGroup = tg =>
  Number(
    tg?.tgClientData?.listPrice ?? tg?.tgListPrice ?? tg?.tgPrice ?? tg?.tgBasePrice ?? tg?.tgNetPrice ?? 0
  );
/** COST tier: what TN charges us. Used only for the Mercury lock bid, never to price the sale. */
const wholesaleUnitPriceFromTicketGroup = tg =>
  Number(tg?.tgWholesalePrice ?? tg?.tgClientData?.wholesalePrice ?? tg?.wholesalePrice ?? 0);
/**
 * Normalize TN ticket groups into the three tiers checkout needs:
 *   tgWholesalePrice  our cost (Mercury lock bid)
 *   tgBasePrice       list / item line, pre-reseller-fee  (== tgListPrice)
 *   tgPrice           all-in per-ticket price shown to the customer
 * Margin = list x (1 + fee) - wholesale, so list must come from the feed, not from cost.
 */
const normalizeTicketGroupPricing = tg => {
  const wholesale = roundMoney(wholesaleUnitPriceFromTicketGroup(tg));
  const listRaw = listUnitPriceFromTicketGroup(tg);
  // Only fall back to cost when the feed carried no list price at all; a list price equal to
  // wholesale is legitimate (TN retail can match wholesale on some groups).
  const list = roundMoney(Number.isFinite(listRaw) && listRaw > 0 ? listRaw : wholesale);
  const allIn = withResellerFee(list);
  return {
    ...tg,
    ...(wholesale > 0 ? { tgWholesalePrice: wholesale } : {}),
    tgListPrice: list,
    tgBasePrice: list,
    tgNetPrice: list,
    tgPrice: allIn,
  };
};

const seatSelectionFromTicketGroup = ticketGroup => {
  const section = String(ticketGroup?.tgUserSec || ticketGroup?.section || '').trim();
  const row = String(ticketGroup?.tgUserRow || ticketGroup?.row || '').trim();
  const notes = String(ticketGroup?.tgNotes || ticketGroup?.notes || '').trim();
  return {
    ...(section ? { section } : {}),
    ...(row ? { row } : {}),
    ...(notes ? { notes } : {}),
  };
};

/**
 * Delivery methods the locked ticket group actually supports, as resolved server-side by
 * /api/tn-seatics/lock-selection. Fulfillment needs these on the transaction: the listing-level
 * default is event-wide and Mercury rejects the order when a group uses a different method.
 */
const deliveryMethodsFromLockData = lockData => {
  const raw = Array.isArray(lockData?.deliveryMethods) ? lockData.deliveryMethods : [];
  const methods = raw.map(m => String(m || '').trim()).filter(Boolean);
  return methods.length ? { deliveryMethods: methods } : {};
};

const emptyRequoteModalState = {
  open: false,
  payload: null,
  pendingCheckoutArgs: null,
};

const formatUsd = amount => {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
};

const buildRequoteMessage = payload => {
  const price = formatUsd(payload?.updatedUnitPrice) || 'the updated price';
  return (
    <>
      We found similar seats for <strong>{price}</strong> in the same section. Would you like to
      purchase these at the updated price before someone else grabs them?
    </>
  );
};

const parseJsonResponse = async res => {
  const contentType = (res.headers.get('content-type') || '').toLowerCase();
  let data = null;
  if (contentType.includes('application/json')) {
    try {
      data = await res.json();
    } catch (e) {
      data = null;
    }
  } else {
    const text = await res.text();
    data = text ? { error: text } : null;
  }
  return { ok: res.ok, status: res.status, data };
};

const postLockSelection = async payload => {
  const res = await fetch('/api/tn-seatics/lock-selection', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return parseJsonResponse(res);
};

const sleepMs = ms => new Promise(resolve => setTimeout(resolve, ms));

/** One bounded retry covers a transient failure without keeping shoppers waiting for minutes. */
const LOCK_SELECTION_MAX_ATTEMPTS = 2;

/**
 * New identity for each checkout attempt, shared by the retries of that attempt.
 * Reusing only listing/group/quantity forever causes Mercury request-ID collisions
 * after the response cache expires, including between different buyers.
 */
const createLockSelectionIdempotencyKey = ({ listingId, eventId, ticketGroupId, quantity }) => {
  const browserCrypto = typeof window !== 'undefined' ? window.crypto : null;
  const attemptNonce = browserCrypto?.randomUUID
    ? browserCrypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random()
        .toString(36)
        .slice(2)}`;
  return `seatics-${listingId}-${eventId}-${ticketGroupId}-${quantity}-${attemptNonce}`;
};

const postLockSelectionWithRetry = async payload => {
  let last = null;
  for (let attempt = 0; attempt < LOCK_SELECTION_MAX_ATTEMPTS; attempt += 1) {
    if (attempt > 0) {
      await sleepMs(lockSelectionRetryDelayMs(attempt));
    }
    try {
      last = await postLockSelection(payload);
    } catch (e) {
      last = {
        ok: false,
        status: 502,
        data: { error: e?.message || 'Network error while locking tickets' },
      };
    }
    if (last.ok && last.data?.lockRequestId) {
      return last;
    }
    if (last.status === 409 && (last.data?.code === 'requote_required' || last.data?.code === 'unavailable')) {
      return last;
    }
    if (!isLockSelectionRetryable(last.status, last.data || {}) || attempt === LOCK_SELECTION_MAX_ATTEMPTS - 1) {
      return last;
    }
    console.warn(
      `[Seatics] lock-selection retry ${attempt + 1}/${LOCK_SELECTION_MAX_ATTEMPTS - 1}`,
      last.status,
      last.data?.code || last.data?.error
    );
  }
  return last;
};

const postInitiateLockedTransaction = async payload => {
  const res = await fetch('/api/tn-seatics/initiate-locked-transaction', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return parseJsonResponse(res);
};

const releaseSeaticsLock = async (lockRequestId, reason = 'requote_declined') => {
  const id = String(lockRequestId || '').trim();
  if (!id) return;
  try {
    await fetch('/api/tn-seatics/release-lock', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ lockRequestId: id, reason }),
    });
  } catch (e) {
    console.warn('[Seatics] failed to release lock after re-quote decline', id, e && e.message);
  }
};

const lockErrorMessage = ({ status, data }) => {
  if (isTnUpstreamTrafficError(status, data)) {
    return TN_TRAFFIC_CUSTOMER_MESSAGE;
  }
  if (status === 409 && data?.code === 'unavailable') {
    return (
      data?.modalMessage ||
      data?.error ||
      'Sorry, those tickets are no longer available. Please choose another ticket group.'
    );
  }
  const combined = [data?.error, data?.message].filter(Boolean).join(' ');
  // Mercury can reject a Seatics-painted TG that just sold out / went holdable=false.
  if (/no tickets available/i.test(combined)) {
    return 'Sorry, those tickets are no longer available. Please choose another ticket group.';
  }
  return data?.error || data?.message || `Lock failed (${status})`;
};

const lockError = result => {
  const err = new Error(lockErrorMessage(result));
  const combined = [result?.data?.error, result?.data?.message, err.message]
    .filter(Boolean)
    .join(' ');
  if (
    (result?.status === 409 && result?.data?.code === 'unavailable') ||
    isTnUpstreamTrafficError(result?.status, result?.data) ||
    /no tickets available/i.test(combined) ||
    /no longer available/i.test(err.message)
  ) {
    err.isFriendlyCheckoutMessage = true;
  }
  return err;
};

const checkoutAlertMessage = err =>
  formatCheckoutAlertMessage(err, { qtyRequiredMessage: SEATICS_CHECKOUT_QTY_REQUIRED_MSG });

const requoteRetryIdempotencyKey = (baseKey, requoteToken) => {
  const base = String(baseKey || '').trim();
  const token = String(requoteToken || '').trim();
  return base && token ? `${base}-requote-${token}` : base;
};

const lockExpiresAtFromLockData = lockData => lockData?.quote?.lockExpiry || null;

const resolveRequoteQuantity = (lockPayload, data, ticketGroup) => {
  const fromLock = Number(lockPayload?.quantity);
  if (Number.isFinite(fromLock) && fromLock >= 1) {
    if (ticketGroup && !ticketGroupAllowsQuantity(ticketGroup, fromLock)) {
      throw checkoutQuantityError();
    }
    return fromLock;
  }
  const fromData = Number(data?.quantity);
  if (Number.isFinite(fromData) && fromData >= 1) {
    if (ticketGroup && !ticketGroupAllowsQuantity(ticketGroup, fromData)) {
      throw checkoutQuantityError();
    }
    return fromData;
  }
  throw checkoutQuantityError();
};

const assertLockPayloadQuantity = (lockPayload, ticketGroup) => {
  const quantity = Number(lockPayload?.quantity);
  if (!Number.isFinite(quantity) || quantity < 1) {
    throw checkoutQuantityError();
  }
  if (ticketGroup && !ticketGroupAllowsQuantity(ticketGroup, quantity)) {
    throw checkoutQuantityError();
  }
  return quantity;
};

const listingForTnCheckout = (listing, unitPriceBase) => {
  if (!listing || !Number.isFinite(unitPriceBase) || unitPriceBase <= 0) {
    return listing;
  }
  const currency = listing.attributes?.price?.currency || 'USD';
  return {
    ...listing,
    attributes: {
      ...listing.attributes,
      price: {
        amount: Math.round(unitPriceBase * 100),
        currency,
      },
    },
  };
};

/**
 * Price tiers for a locked selection. The server quote wins over the client's selection: it
 * reflects the live ticket group after the price gate, including a same-section replacement.
 * `listUnitPrice` prices the Sharetribe item line; `wholesaleUnitPrice` is cost, for the
 * Mercury lock only. Deriving list from all-in is a last resort for older lock responses.
 */
const lockedTiersFromQuote = (quote, fallback = {}) => {
  const positive = v => {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? roundMoney(n) : 0;
  };
  const allInUnitPrice = positive(quote?.unitPrice) || positive(fallback.allInUnitPrice);
  const listUnitPrice =
    positive(quote?.listUnitPrice) ||
    positive(fallback.listUnitPrice) ||
    (allInUnitPrice ? withoutResellerFee(allInUnitPrice) : 0);
  const wholesaleUnitPrice =
    positive(quote?.wholesaleUnitPrice) || positive(fallback.wholesaleUnitPrice);
  return { allInUnitPrice, listUnitPrice, wholesaleUnitPrice };
};

const buildAcceptedRequotePayload = (lockPayload, data, ticketGroup = null) => {
  const requoteToken = String(data?.requoteToken || '').trim();
  const ticketGroupId = String(data?.ticketGroupId || '').trim();
  const allInUnitPrice = Number(data?.updatedUnitPrice);
  const quantity = resolveRequoteQuantity(lockPayload, data, ticketGroup);

  if (!requoteToken || !ticketGroupId || !Number.isFinite(allInUnitPrice)) {
    throw new Error('The updated ticket quote was incomplete. Please try again.');
  }

  // Requote prices are customer-facing all-in. The item line needs the list tier and
  // fulfillment needs wholesale; the backend sends both alongside the all-in price.
  const tiers = lockedTiersFromQuote({
    unitPrice: allInUnitPrice,
    listUnitPrice: data?.updatedListUnitPrice ?? data?.updatedBaseUnitPrice,
    wholesaleUnitPrice: data?.updatedWholesaleUnitPrice,
  });

  return {
    ticketGroupId,
    quantity,
    ...tiers,
    payload: {
      ...lockPayload,
      idempotencyKey: requoteRetryIdempotencyKey(lockPayload?.idempotencyKey, requoteToken),
      acceptRequote: true,
      requoteToken,
      ticketGroupId,
      unitPrice: allInUnitPrice,
      quantity,
    },
  };
};

const lockFromAcceptedRequote = async (
  lockPayload,
  data,
  requestRequoteDecision,
  options = {}
) => {
  const ticketGroup = options.ticketGroup || null;
  assertLockPayloadQuantity(lockPayload, ticketGroup);

  hidePageLoadIndicator();
  const accepted =
    typeof requestRequoteDecision === 'function'
      ? await requestRequoteDecision(data, { lockPayload })
      : false;
  const releaseLockRequestId = String(options.releaseLockRequestId || data?.lockRequestId || '').trim();

  if (!accepted) {
    await releaseSeaticsLock(releaseLockRequestId);
    return { cancelled: true };
  }

  showPageLoadIndicator({ persist: true });
  const acceptedRequote = buildAcceptedRequotePayload(lockPayload, data, ticketGroup);
  const retryLock = await postLockSelectionWithRetry(acceptedRequote.payload);
  if (!retryLock.ok || !retryLock.data?.lockRequestId) {
    await releaseSeaticsLock(releaseLockRequestId, 'requote_retry_failed');
    throw lockError(retryLock);
  }

  if (
    releaseLockRequestId &&
    String(retryLock.data.lockRequestId) !== String(releaseLockRequestId)
  ) {
    await releaseSeaticsLock(releaseLockRequestId, 'requote_replaced');
  }

  return {
    lockData: retryLock.data,
    ticketGroupId: acceptedRequote.ticketGroupId,
    quantity: acceptedRequote.quantity,
    ...lockedTiersFromQuote(retryLock.data?.quote, acceptedRequote),
  };
};

const lockSelectionWithRequote = async (lockPayload, requestRequoteDecision, options = {}) => {
  const ticketGroup = options.ticketGroup || null;
  assertLockPayloadQuantity(lockPayload, ticketGroup);

  // Transient Mercury/TN failures (throttle, timeout, race) are retried silently with the
  // spinner still up — do not flash the traffic modal until attempts are exhausted.
  const firstLock = await postLockSelectionWithRetry(lockPayload);
  if (firstLock.ok && firstLock.data?.lockRequestId) {
    return {
      lockData: firstLock.data,
      ticketGroupId: lockPayload.ticketGroupId,
      quantity: lockPayload.quantity,
      ...lockedTiersFromQuote(firstLock.data?.quote, {
        allInUnitPrice: lockPayload.unitPrice,
        listUnitPrice: options.listUnitPrice,
        wholesaleUnitPrice: options.wholesaleUnitPrice,
      }),
    };
  }

  const data = firstLock.data || {};
  if (firstLock.status === 409 && data.code === 'requote_required') {
    return lockFromAcceptedRequote(lockPayload, data, requestRequoteDecision, options);
  }

  throw lockError(firstLock);
};

const buildInitiatePayload = (basePayload, locked, ticketGroup = null) => {
  const lockExpiresAt = lockExpiresAtFromLockData(locked.lockData);
  const { lockExpiresAt: _lockExpiresAt, lockExpiry: _lockExpiry, ...rest } = basePayload;
  return {
    ...rest,
    lockRequestId: locked.lockData.lockRequestId,
    ticketGroupId: locked.ticketGroupId,
    quantity: locked.quantity,
    // The initiate endpoint expects the customer all-in unit and derives the item line itself.
    unitPrice: locked.allInUnitPrice,
    listUnitPrice: locked.listUnitPrice,
    ...(locked.wholesaleUnitPrice > 0 ? { wholesaleUnitPrice: locked.wholesaleUnitPrice } : {}),
    ...(lockExpiresAt ? { lockExpiresAt: String(lockExpiresAt) } : {}),
    ...seatSelectionFromTicketGroup(ticketGroup || locked.ticketGroup || null),
    ...deliveryMethodsFromLockData(locked.lockData),
  };
};

const initiateLockedTransactionWithRequote = async ({
  lockPayload,
  initBasePayload,
  locked,
  requestRequoteDecision,
  ticketGroup = null,
}) => {
  let currentLocked = locked;
  let initPayload = buildInitiatePayload(initBasePayload, currentLocked, ticketGroup);

  for (let attempt = 0; attempt < 2; attempt += 1) {
    const initResult = await postInitiateLockedTransaction(initPayload);
    const initData = initResult.data;
    const txId = initData?.transaction?.id?.uuid || initData?.transaction?.id || null;

    if (initResult.ok && txId) {
      return { initData, txId };
    }

    if (initResult.status === 409 && initData?.code === 'requote_required' && attempt === 0) {
      const nextLocked = await lockFromAcceptedRequote(lockPayload, initData, requestRequoteDecision, {
        releaseLockRequestId: currentLocked.lockData.lockRequestId,
        ticketGroup,
      });
      if (nextLocked.cancelled) return { cancelled: true };
      currentLocked = nextLocked;
      initPayload = buildInitiatePayload(initBasePayload, currentLocked, ticketGroup);
    } else if (initResult.status === 409 && initData?.code === 'unavailable') {
      throw lockError(initResult);
    } else {
      if (isTnUpstreamTrafficError(initResult.status, initData)) {
        const err = new Error(TN_TRAFFIC_CUSTOMER_MESSAGE);
        err.isFriendlyCheckoutMessage = true;
        throw err;
      }
      throw new Error(initData?.error || `Checkout init failed (${initResult.status})`);
    }
  }

  throw new Error('Checkout init failed after accepting the updated quote.');
};

const valueContainsSchemaEvent = value => {
  if (!value) return false;
  if (Array.isArray(value)) return value.some(valueContainsSchemaEvent);
  if (typeof value === 'object') {
    const type = value['@type'] || value.type;
    if (Array.isArray(type) ? type.includes('Event') : type === 'Event') {
      return true;
    }
    return Object.values(value).some(valueContainsSchemaEvent);
  }
  return false;
};

const scriptContainsSchemaEvent = script => {
  const text = script?.textContent || '';
  if (!text.trim()) return false;
  try {
    return valueContainsSchemaEvent(JSON.parse(text));
  } catch (e) {
    return /"@type"\s*:\s*"Event"|schema\.org\/Event|maps\.seatics\.com/i.test(text);
  }
};

const stripMicrodataFromNode = node => {
  if (!node || typeof node.querySelectorAll !== 'function') return;
  const selector = '[itemscope], [itemtype], [itemprop]';
  const nodes =
    node.matches && node.matches(selector)
      ? [node, ...node.querySelectorAll(selector)]
      : [...node.querySelectorAll(selector)];
  nodes.forEach(el => {
    const itemType = String(el.getAttribute('itemtype') || '');
    if (/schema\.org\/Event/i.test(itemType) || el.closest?.(`#${SEATICS_MAP_CONTAINER_ID}, #sea-mp`)) {
      el.removeAttribute('itemscope');
      el.removeAttribute('itemtype');
      el.removeAttribute('itemprop');
    }
  });
};

const removeThirdPartyEventStructuredData = () => {
  if (typeof document === 'undefined') return;
  document.querySelectorAll('script[type="application/ld+json"]').forEach(script => {
    if (script.id === 'page-schema') return;
    if (scriptContainsSchemaEvent(script)) script.remove();
  });
  const mapRoot = document.getElementById(SEATICS_MAP_CONTAINER_ID);
  if (mapRoot) stripMicrodataFromNode(mapRoot);
  const seaMp = document.getElementById('sea-mp');
  if (seaMp) stripMicrodataFromNode(seaMp);
};

const installStructuredDataGuard = isCancelled => {
  removeThirdPartyEventStructuredData();
  if (typeof MutationObserver === 'undefined' || typeof document === 'undefined') return () => {};
  let cleanupTimer = null;
  const scheduleCleanup = () => {
    if (cleanupTimer) return;
    cleanupTimer = setTimeout(() => {
      cleanupTimer = null;
      if (!isCancelled()) removeThirdPartyEventStructuredData();
    }, 0);
  };
  const observer = new MutationObserver(mutations => {
    if (isCancelled()) return;
    const shouldClean = mutations.some(mutation => {
      if (mutation.type === 'characterData' || mutation.type === 'attributes') return true;
      return [...mutation.addedNodes].some(node => {
        if (node.nodeType === 3) return true;
        if (node.nodeType !== 1) return false;
        return (
          node.matches?.('script, [itemscope], [itemtype], [itemprop]') ||
          node.querySelector?.('script, [itemscope], [itemtype], [itemprop]')
        );
      });
    });
    if (shouldClean) scheduleCleanup();
  });
  observer.observe(document.body, {
    attributes: true,
    attributeFilter: ['itemtype', 'itemscope', 'itemprop', 'type'],
    characterData: true,
    childList: true,
    subtree: true,
  });
  const reapplyTimer = setInterval(() => {
    if (isCancelled()) {
      clearInterval(reapplyTimer);
      return;
    }
    removeThirdPartyEventStructuredData();
  }, 500);
  setTimeout(() => clearInterval(reapplyTimer), 15000);
  return () => {
    if (cleanupTimer) clearTimeout(cleanupTimer);
    clearInterval(reapplyTimer);
    observer.disconnect();
  };
};

/** Seatics js2 expects jQuery in the same document as MapAndLayout (no iframe). */
const seaticsJqueryUrl =
  (typeof process !== 'undefined' && process.env.REACT_APP_SEATICS_JQUERY_URL) ||
  'https://code.jquery.com/jquery-3.6.4.min.js';

const SEATICS_MAP_CONTAINER_ID = 'tn-maps';
const SEATICS_BOOTSTRAP_SCRIPT_ATTR = 'data-promotix-seatics-bootstrap';
const SEATICS_JQUERY_SCRIPT_ATTR = 'data-promotix-seatics-jquery';
const SEATICS_LAYOUT_STYLE_ATTR = 'data-promotix-seatics-layout-style';
const SEATICS_LAYOUT_STYLE_VERSION = '56';
const SEATICS_INSTANCE_ATTR = 'data-promotix-seatics-instance';
const SEATICS_EMBED_BODY_ATTR = 'data-promotix-seatics-embedded';
const SEATICS_TICKET_DATA_STATE_KEY = '__promotixSeaticsTicketDataState';
const PROMOTIX_MOBILE_TRUST_ATTR = 'data-promotix-mobile-trust';
/** Stable mount we own — never rewrite headline/subcopy children as hosts. */
const PROMOTIX_MOBILE_TRUST_MOUNT_ID = 'promotix-mobile-trust-mount';
/** TN ⓘ-only blurb row under Show Map — hidden once trust copy lives in the header. */
const PROMOTIX_HIDE_INFO_SHELL_ATTR = 'data-promotix-hide-info-shell';
const PROMOTIX_DATE_BOLD_ATTR = 'data-promotix-date-bold';
const SEE_OTHER_DATES_ATTR = 'data-promotix-see-other-dates';
const SEE_OTHER_DATES_WRAP_ATTR = 'data-promotix-see-other-wrap';

/** Spinner/status chrome while MapAndLayout boots. When a static map URL is available we
 *  keep this overlay translucent so the SSR `<img>` remains the visible LCP candidate. */
const SeaticsMapLoadingPlaceholder = ({ overStaticMap }) => (
  <div
    className={classNames(css.seaticsMapLoading, overStaticMap && css.seaticsMapLoadingOverMap)}
    role="status"
    aria-live="polite"
  >
    <div
      className={classNames(
        css.seaticsMapLoadingStack,
        overStaticMap && css.seaticsMapLoadingBadge
      )}
    >
      <div className={css.seaticsMapSpinner} aria-hidden="true" />
      <p className={css.seaticsMapLoadingText}>Getting available ticket options</p>
    </div>
  </div>
);

const seaticsMapSurfaceReady = () => {
  if (typeof document === 'undefined') {
    return false;
  }
  const root = document.getElementById(SEATICS_MAP_CONTAINER_ID);
  if (root?.querySelector('img, canvas, svg')) {
    return true;
  }
  const seaMp = document.getElementById('sea-mp');
  if (seaMp?.querySelector('img, table, tr, button')) {
    return true;
  }
  return Boolean(root && root.childElementCount > 0 && String(root.textContent || '').trim().length > 24);
};

const promotixPoliciesUrl = () => {
  const root = String(process.env.REACT_APP_MARKETPLACE_ROOT_URL || '').replace(/\/$/, '');
  if (root) return `${root}/policies`;
  return 'https://www.promotix.com/policies';
};

let nativeMobileViewportCache = null;

/**
 * Same breakpoint as `SeaticsMapEmbed` (native Seatics mobile vs desktop chrome).
 * Cached until resize: reading innerWidth between the patchers' DOM writes forced a
 * style recalc of the whole ticket list on every call (~16ms each on a mid phone).
 */
const isSeaticsNativeMobileViewport = () => {
  if (typeof window === 'undefined') return false;
  if (nativeMobileViewportCache === null) {
    nativeMobileViewportCache = window.innerWidth < 992;
    window.addEventListener(
      'resize',
      () => {
        nativeMobileViewportCache = null;
      },
      { once: true }
    );
  }
  return nativeMobileViewportCache;
};

const SEATICS_DISCLOSURE_BODY =
  'All tickets are guaranteed valid for entry and protected by a 100% money back guarantee. Price includes all fees. No additional fees are required at checkout. Refund protection for many unforeseen situations is optional at checkout. Ticket prices may be above or below face value. This ticket is being sold on the PromoTix resale marketplace, not from the box office or venue.';

const SEATICS_DESKTOP_DISCLOSURE_FOLLOW_BODY = SEATICS_DISCLOSURE_BODY;

/** Mobile trust strip above inventory (Seatics native mobile chrome). */
const SEATICS_MOBILE_TRUST_HEADLINE_LINE1 = '✓ 100% Money-Back Guarantee';
const SEATICS_MOBILE_TRUST_HEADLINE_LINE2 = 'Final prices below · Secure checkout';
/** Full headline (plain-text contexts); HTML uses an explicit <br> after Guarantee. */
const SEATICS_MOBILE_TRUST_HEADLINE = `${SEATICS_MOBILE_TRUST_HEADLINE_LINE1} · ${SEATICS_MOBILE_TRUST_HEADLINE_LINE2}`;
const SEATICS_MOBILE_TRUST_SUBCOPY =
  'PromoTix is a verified marketplace, not the venue. Prices may be above or below face value.';
/** Plain-text fallback for Seatics.Text string scrubbing (not HTML). */
const SEATICS_MOBILE_TRUST_PLAIN = `${SEATICS_MOBILE_TRUST_HEADLINE_LINE1}. ${SEATICS_MOBILE_TRUST_HEADLINE_LINE2}. ${SEATICS_MOBILE_TRUST_SUBCOPY}`;

/** TN MapAndLayout default copy (mobile chrome); not always overridden by `mobileEventInfoRightCol`. */
const SEATICS_LEGACY_DISCLOSURE_SNIPPETS = [
  'Includes all fees. No additional fees are required at checkout. Refund protection is optional. Ticket prices may be above or below face value. This ticket is being sold on the PromoTix resale marketplace, not from the box office or venue.',
  '100% Money-Back Guarantee. Includes all fees. No additional fees are required at checkout. Refund protection is optional. Ticket prices may be above or below face value. This ticket is being sold on the PromoTix resale marketplace, not from the box office or venue.',
];

const SEATICS_LEGACY_MOBILE_RESALE_SNIPPETS = [
  'We are a resale marketplace, not the venue. Prices are set by sellers and may be above or below face value.',
  "We're a resale marketplace, not the venue. Prices are set by sellers and may be above or below face value.",
];

const applySeaticsLegacyResaleSnippetReplacements = str => {
  let out = str;
  SEATICS_LEGACY_DISCLOSURE_SNIPPETS.forEach(snippet => {
    if (out.includes(snippet)) {
      out = out.split(snippet).join(SEATICS_DISCLOSURE_BODY);
    }
  });
  SEATICS_LEGACY_MOBILE_RESALE_SNIPPETS.forEach(snippet => {
    if (out.includes(snippet)) {
      out = out.split(snippet).join(SEATICS_MOBILE_TRUST_PLAIN);
    }
  });
  return out;
};

/** HTML for Seatics desktop right column: guarantee control + modal + disclosure. */
const buildDesktopEventInfoRightColHtml = () =>
  '<h2 class="sea-offscreen">Disclaimer</h2>' +
  '<div class="event-info-disclaimer" style="padding:6px 12px;">' +
  '<button type="button" aria-controls="event-info-guarantee" aria-expanded="false" id="event-info-guarantee-show" ' +
  'class="sea-accessible-button event-info-guarantee-show" ' +
  'style="color:#e67615;font-size:16px !important;font-weight:600;line-height:1.4;display:block;margin:0 0 6px 0;background:none;border:none;padding:0;cursor:pointer;text-align:left;">' +
  '☞ 100% Money-Back Guarantee</button><br />' +
  '<div style="font-size:12px !important;line-height:1.28;color:#333;">' +
  '<p style="margin:0;font-size:12px !important;line-height:1.28;">' +
  SEATICS_DESKTOP_DISCLOSURE_FOLLOW_BODY +
  '</p>' +
  '</div>' +
  '<div id="event-info-guarantee-backdrop" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,.45);z-index:99998;"></div>' +
  '<div role="dialog" aria-modal="true" aria-labelledby="event-info-guarantee-title" tabindex="-1" id="event-info-guarantee" class="event-info-guarantee" style="display:none;box-sizing:border-box;padding:12px 14px;max-width:560px;width:min(calc(100vw - 32px),560px);position:fixed;left:50%;top:50%;transform:translate(-50%,-50%);background:#fff;border-radius:8px;box-shadow:0 16px 36px rgba(0,0,0,.35);z-index:99999;max-height:80vh;overflow:auto;">' +
  '<button type="button" id="event-info-guarantee-close" class="event-info-guarantee-close icon-cancel" aria-label="Close" ' +
  'style="position:absolute;top:8px;right:10px;border:none;background:transparent;padding:4px 8px;margin:0;cursor:pointer;font-size:20px;line-height:1;color:#666;z-index:5;">&times;</button>' +
  '<div id="event-info-guarantee-title" style="font-size:16px !important;font-weight:700;line-height:1.35;color:#222;margin:0 0 12px 0;padding-right:36px;">' +
  'Shop with Confidence on PromoTix</div>' +
  '<div style="font-size:12px !important;line-height:1.5;color:#333;">' +
  '<p style="margin:0 0 10px 0;font-size:12px !important;line-height:1.5;">PromoTix guarantees that:</p>' +
  '<p style="margin:0 0 6px 0;font-size:12px !important;line-height:1.5;">&#8226;&nbsp;Your tickets will be legitimate and valid for entry at the event.(1)</p>' +
  '<p style="margin:0 0 6px 0;font-size:12px !important;line-height:1.5;">&#8226;&nbsp;Your tickets will be delivered in time for the event.</p>' +
  '<p style="margin:0 0 12px 0;font-size:12px !important;line-height:1.5;">&#8226;&nbsp;You will receive a 100% refund if your event is cancelled and not rescheduled.(2)</p>' +
  '</div>' +
  '<div style="font-size:8px !important;line-height:1.5;color:#555;">' +
  '<p style="margin:0 0 6px 0;font-weight:600;font-size:8px !important;line-height:1.5;">Notes:</p>' +
  '<p style="margin:0 0 6px 0;font-size:8px !important;line-height:1.5;">(1) Verified proof must be provided in letter or email form from the venue. ' +
  'Written or stamped &quot;voids&quot; do not qualify as verified proof.</p>' +
  '<p style="margin:0;font-size:8px !important;line-height:1.5;">(2) In some cases, you may need to return the tickets before a refund will be issued.</p>' +
  '</div></div></div>';

/** Inner markup for the mobile trust strip (bold 2-line headline + non-bold subcopy). */
const buildMobileEventInfoDisclaimerInnerHtml = () =>
  // Use divs (not <p>) so a mistaken nested rewrite cannot be "fixed" by the browser into an empty shell.
  '<div class="promotix-mobile-trust-headline" style="display:block !important;visibility:visible !important;margin:0 0 6px 0 !important;font-size:14px !important;font-weight:700 !important;line-height:1.35 !important;color:var(--marketplaceColor, #ff822f) !important;text-align:left !important;">' +
  `${SEATICS_MOBILE_TRUST_HEADLINE_LINE1}<br />${SEATICS_MOBILE_TRUST_HEADLINE_LINE2}` +
  '</div>' +
  '<div class="promotix-mobile-trust-subcopy" style="display:block !important;visibility:visible !important;opacity:1 !important;margin:0 !important;font-size:12px !important;font-weight:400 !important;line-height:1.35 !important;color:#333 !important;text-align:left !important;">' +
  SEATICS_MOBILE_TRUST_SUBCOPY +
  '</div>';

/** Seatics native mobile: trust headline + marketplace subcopy (no guarantee modal). */
const buildMobileEventInfoRightColHtml = () =>
  '<h2 class="sea-offscreen">Disclaimer</h2>' +
  `<div id="${PROMOTIX_MOBILE_TRUST_MOUNT_ID}" class="event-info-disclaimer promotix-mobile-trust-strip" ${PROMOTIX_MOBILE_TRUST_ATTR}="1" style="display:block !important;padding:8px 12px;box-sizing:border-box;overflow:visible;">` +
  buildMobileEventInfoDisclaimerInnerHtml() +
  '</div>';

const mobileTrustStripIsComplete = host => {
  if (!(host instanceof HTMLElement)) return false;
  const hasHeadline = !!host.querySelector(':scope > .promotix-mobile-trust-headline, .promotix-mobile-trust-headline');
  const hasSubcopy = !!host.querySelector(':scope > .promotix-mobile-trust-subcopy, .promotix-mobile-trust-subcopy');
  const text = host.textContent || '';
  return (
    hasHeadline &&
    hasSubcopy &&
    text.includes('100% Money-Back Guarantee') &&
    text.includes('Final prices below') &&
    text.includes('verified marketplace')
  );
};

const isOurMobileTrustChrome = el => {
  if (!(el instanceof HTMLElement)) return false;
  if (el.id === PROMOTIX_MOBILE_TRUST_MOUNT_ID) return true;
  if (el.classList.contains('promotix-mobile-trust-headline')) return true;
  if (el.classList.contains('promotix-mobile-trust-subcopy')) return true;
  if (el.classList.contains('promotix-mobile-trust-strip') && el.getAttribute(PROMOTIX_MOBILE_TRUST_ATTR) === '1') {
    return true;
  }
  return !!el.closest?.(`#${PROMOTIX_MOBILE_TRUST_MOUNT_ID}`);
};

/** TN disclaimer slots are often single-line height with overflow:hidden — open ancestors so 3 lines fit. */
const unclipMobileTrustAncestors = mount => {
  if (!(mount instanceof HTMLElement) || typeof window === 'undefined') return;
  let el = mount;
  for (let depth = 0; depth < 10 && el instanceof HTMLElement; depth += 1) {
    if (el.id === 'sea-mp' || el.id === SEATICS_MAP_CONTAINER_ID) break;

    // Undo a mistaken hide on a parent that now holds trust copy.
    if (el.getAttribute(PROMOTIX_HIDE_INFO_SHELL_ATTR) === '1') {
      el.removeAttribute(PROMOTIX_HIDE_INFO_SHELL_ATTR);
      el.removeAttribute('aria-hidden');
      el.style.removeProperty('display');
      el.style.removeProperty('visibility');
      el.style.removeProperty('height');
      el.style.removeProperty('max-height');
      el.style.removeProperty('margin');
      el.style.removeProperty('padding');
      el.style.removeProperty('overflow');
    }

    // Clear clipping only — do not force display:block (breaks TN flex rows).
    el.style.setProperty('overflow', 'visible', 'important');
    el.style.setProperty('overflow-x', 'visible', 'important');
    el.style.setProperty('overflow-y', 'visible', 'important');
    el.style.setProperty('max-height', 'none', 'important');
    el.style.setProperty('line-clamp', 'unset', 'important');
    el.style.setProperty('-webkit-line-clamp', 'unset', 'important');
    el.style.setProperty('clip', 'auto', 'important');
    el.style.setProperty('clip-path', 'none', 'important');

    const cs = window.getComputedStyle(el);
    const fixedOrCappedHeight =
      (cs.height && cs.height !== 'auto' && parseFloat(cs.height) > 0 && parseFloat(cs.height) < 120) ||
      (cs.maxHeight && cs.maxHeight !== 'none' && parseFloat(cs.maxHeight) < 120);
    if (fixedOrCappedHeight) {
      el.style.setProperty('height', 'auto', 'important');
      el.style.setProperty('max-height', 'none', 'important');
    }
    el.classList.add('promotix-mobile-trust-unclipped');

    el = el.parentElement;
  }
};

const syncMobileTrustMountContent = mount => {
  if (!(mount instanceof HTMLElement)) return;
  mount.id = PROMOTIX_MOBILE_TRUST_MOUNT_ID;
  mount.classList.add('event-info-disclaimer', 'promotix-mobile-trust-strip');
  mount.setAttribute(PROMOTIX_MOBILE_TRUST_ATTR, '1');
  mount.removeAttribute(PROMOTIX_HIDE_INFO_SHELL_ATTR);
  mount.removeAttribute('aria-hidden');
  mount.style.setProperty('display', 'block', 'important');
  mount.style.setProperty('visibility', 'visible', 'important');
  mount.style.setProperty('opacity', '1', 'important');
  mount.style.setProperty('overflow', 'visible', 'important');
  mount.style.setProperty('overflow-x', 'visible', 'important');
  mount.style.setProperty('overflow-y', 'visible', 'important');
  mount.style.setProperty('height', 'auto', 'important');
  mount.style.setProperty('max-height', 'none', 'important');
  mount.style.setProperty('min-height', '0', 'important');
  mount.style.setProperty('padding', '8px 12px 12px 12px', 'important');
  mount.style.setProperty('margin', '0', 'important');
  mount.style.setProperty('box-sizing', 'border-box', 'important');
  mount.style.setProperty('width', '100%', 'important');
  mount.style.setProperty('color', '#222', 'important');
  mount.style.setProperty('font-size', '14px', 'important');
  mount.style.setProperty('line-clamp', 'unset', 'important');
  mount.style.setProperty('-webkit-line-clamp', 'unset', 'important');
  mount.style.setProperty('white-space', 'normal', 'important');
  if (!mobileTrustStripIsComplete(mount)) {
    mount.innerHTML = buildMobileEventInfoDisclaimerInnerHtml();
  }
  unclipMobileTrustAncestors(mount);
};

const hostKeepsInfoChrome = host => {
  if (!(host instanceof HTMLElement)) return false;
  return !!host.querySelector(
    'button, a[href], [role="button"], svg, img, i.icon-info, [class*="icon-info"], [class*="info-icon"]'
  );
};

/** Place/repair our owned mount inside an anchor without wiping TN's ⓘ control. */
const placeMobileTrustMountInAnchor = anchor => {
  if (!(anchor instanceof HTMLElement)) return;
  if (isOurMobileTrustChrome(anchor) && anchor.id === PROMOTIX_MOBILE_TRUST_MOUNT_ID) {
    syncMobileTrustMountContent(anchor);
    return;
  }

  let mount =
    anchor.id === PROMOTIX_MOBILE_TRUST_MOUNT_ID
      ? anchor
      : anchor.querySelector(`#${PROMOTIX_MOBILE_TRUST_MOUNT_ID}`);

  if (!mount) {
    const existing = typeof document !== 'undefined' ? document.getElementById(PROMOTIX_MOBILE_TRUST_MOUNT_ID) : null;
    if (existing instanceof HTMLElement && (!existing.isConnected || !anchor.contains(existing))) {
      existing.remove();
    }
    mount = document.createElement('div');
  }

  syncMobileTrustMountContent(mount);

  if (hostKeepsInfoChrome(anchor)) {
    // Drop TN text siblings (truncated blurb) but keep icon/button chrome.
    Array.from(anchor.childNodes).forEach(node => {
      if (node === mount) return;
      if (node.nodeType === Node.TEXT_NODE) {
        node.textContent = '';
        return;
      }
      if (!(node instanceof HTMLElement)) return;
      if (node.id === PROMOTIX_MOBILE_TRUST_MOUNT_ID) return;
      if (
        node.matches('button, a, [role="button"]') ||
        node.querySelector('button, a, [role="button"], svg, img')
      ) {
        return;
      }
      const ct = (node.textContent || '').replace(/\s+/g, ' ').trim();
      if (
        !ct ||
        looksLikeLegacyMobileResaleBlurb(ct) ||
        looksLikeLegacyLongDisclosure(ct) ||
        /money[- ]?back guarantee/i.test(ct) ||
        /final prices below/i.test(ct) ||
        /resale marketplace/i.test(ct) ||
        /face value/i.test(ct)
      ) {
        node.remove();
      }
    });
    if (!anchor.contains(mount)) {
      anchor.appendChild(mount);
    }
    anchor.classList.add('promotix-mobile-trust-host');
    return;
  }

  // Text-only host: make the host our mount (single owned node).
  if (anchor !== mount) {
    syncMobileTrustMountContent(anchor);
  }
};

/** @deprecated name kept for call sites — routes through mount placement. */
const applyMobileTrustStripToHost = host => {
  placeMobileTrustMountInAnchor(host);
};

const patchSeaticsPreCheckoutCta = () => {
  if (typeof document === 'undefined') return;
  [SEATICS_MAP_CONTAINER_ID, `${SEATICS_MAP_CONTAINER_ID}-inner`, 'tn-maps-inner', 'sea-mp'].forEach(
    id => {
      const el = document.getElementById(id);
      if (el) patchPreCheckoutCtaInRoot(el);
    }
  );
  // Quantity panel can portal outside the map roots on some mobile layouts.
  if (document.body) {
    patchPreCheckoutCtaInRoot(document.body);
  }
};

const ensureSeaticsLayoutStyles = () => {
  if (typeof document === 'undefined') return;
  const styleSelector = `style[${SEATICS_LAYOUT_STYLE_ATTR}="${SEATICS_LAYOUT_STYLE_VERSION}"]`;
  let s = document.querySelector(styleSelector);
  if (!s) {
    document
      .querySelectorAll(`style[${SEATICS_LAYOUT_STYLE_ATTR}]`)
      .forEach(el => el.remove());
    s = document.createElement('style');
    s.setAttribute(SEATICS_LAYOUT_STYLE_ATTR, SEATICS_LAYOUT_STYLE_VERSION);
    (document.head || document.body).appendChild(s);
  }
  s.textContent = `
#${SEATICS_MAP_CONTAINER_ID} .event-info-disclaimer p,
body[${SEATICS_EMBED_BODY_ATTR}="1"] #sea-mp .event-info-disclaimer p {
  margin-block-start: 0 !important;
  margin-block-end: 0 !important;
}
#${SEATICS_MAP_CONTAINER_ID} .promotix-mobile-trust-headline,
#sea-mp .promotix-mobile-trust-headline,
body[${SEATICS_EMBED_BODY_ATTR}="1"] #sea-mp .promotix-mobile-trust-headline {
  display: block !important;
  visibility: visible !important;
  margin: 0 0 6px 0 !important;
  font-size: 14px !important;
  font-weight: 700 !important;
  line-height: 1.35 !important;
  color: var(--marketplaceColor, #ff822f) !important;
  text-align: left !important;
}
#${SEATICS_MAP_CONTAINER_ID} .promotix-mobile-trust-subcopy,
#sea-mp .promotix-mobile-trust-subcopy,
body[${SEATICS_EMBED_BODY_ATTR}="1"] #sea-mp .promotix-mobile-trust-subcopy {
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;
  height: auto !important;
  max-height: none !important;
  overflow: visible !important;
  margin: 0 !important;
  padding: 0 0 2px 0 !important;
  font-size: 12px !important;
  font-weight: 400 !important;
  line-height: 1.4 !important;
  color: #333 !important;
  text-align: left !important;
  white-space: normal !important;
  -webkit-line-clamp: unset !important;
  line-clamp: unset !important;
}
/* Bold Seatics date/time (mobile above Show Map + desktop upper-right box).
   Match trust-headline size (14px). */
#${SEATICS_MAP_CONTAINER_ID} .event-info-date-time-span,
#${SEATICS_MAP_CONTAINER_ID} .event-info-date-time-span span,
#${SEATICS_MAP_CONTAINER_ID} [${PROMOTIX_DATE_BOLD_ATTR}="1"],
#${SEATICS_MAP_CONTAINER_ID} [${PROMOTIX_DATE_BOLD_ATTR}="1"] span,
#sea-mp .event-info-date-time-span,
#sea-mp .event-info-date-time-span span,
#sea-mp [${PROMOTIX_DATE_BOLD_ATTR}="1"],
#sea-mp [${PROMOTIX_DATE_BOLD_ATTR}="1"] span,
body[${SEATICS_EMBED_BODY_ATTR}="1"] #sea-mp .event-info-date-time-span,
body[${SEATICS_EMBED_BODY_ATTR}="1"] #sea-mp .event-info-date-time-span span {
  font-size: 14px !important;
  font-weight: 700 !important;
  line-height: 1.35 !important;
}
#sea-mp .promotix-mobile-trust-strip,
#sea-mp #${PROMOTIX_MOBILE_TRUST_MOUNT_ID},
#sea-mp .event-info-disclaimer[${PROMOTIX_MOBILE_TRUST_ATTR}="1"],
body[${SEATICS_EMBED_BODY_ATTR}="1"] #sea-mp .promotix-mobile-trust-strip,
body[${SEATICS_EMBED_BODY_ATTR}="1"] #sea-mp #${PROMOTIX_MOBILE_TRUST_MOUNT_ID} {
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;
  overflow: visible !important;
  overflow-x: visible !important;
  overflow-y: visible !important;
  height: auto !important;
  max-height: none !important;
  min-height: 0 !important;
  white-space: normal !important;
  -webkit-line-clamp: unset !important;
  line-clamp: unset !important;
  color: #222 !important;
}
#sea-mp .promotix-mobile-trust-host,
body[${SEATICS_EMBED_BODY_ATTR}="1"] #sea-mp .promotix-mobile-trust-host {
  visibility: visible !important;
  overflow: visible !important;
  overflow-x: visible !important;
  overflow-y: visible !important;
  height: auto !important;
  max-height: none !important;
  white-space: normal !important;
  -webkit-line-clamp: unset !important;
  line-clamp: unset !important;
}
#sea-mp .promotix-mobile-trust-unclipped,
body[${SEATICS_EMBED_BODY_ATTR}="1"] #sea-mp .promotix-mobile-trust-unclipped {
  overflow: visible !important;
  overflow-x: visible !important;
  overflow-y: visible !important;
  max-height: none !important;
  -webkit-line-clamp: unset !important;
  line-clamp: unset !important;
}
/* Drop the empty TN ⓘ row under Show Map (trust copy is already in the event header). */
#sea-mp [${PROMOTIX_HIDE_INFO_SHELL_ATTR}="1"],
body[${SEATICS_EMBED_BODY_ATTR}="1"] #sea-mp [${PROMOTIX_HIDE_INFO_SHELL_ATTR}="1"],
#${SEATICS_MAP_CONTAINER_ID} [${PROMOTIX_HIDE_INFO_SHELL_ATTR}="1"] {
  display: none !important;
  visibility: hidden !important;
  height: 0 !important;
  max-height: 0 !important;
  margin: 0 !important;
  padding: 0 !important;
  overflow: hidden !important;
  border: 0 !important;
}
/*
 * Same placement on mobile + desktop: sibling immediately after the date/time
 * host (not inside it — TN often clips/rewrites the span's children on desktop).
 */
a.promotix-see-other-dates {
  display: block !important;
  position: static !important;
  margin: 4px 0 0 0 !important;
  padding: 0 !important;
  color: var(--marketplaceColor, #ff822f) !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  line-height: 1.3 !important;
  text-decoration: underline !important;
  background: none !important;
  border: none !important;
  cursor: pointer !important;
  float: none !important;
  clear: both !important;
  width: auto !important;
  max-width: 100% !important;
  white-space: normal !important;
  z-index: auto !important;
}
a.promotix-see-other-dates:hover,
a.promotix-see-other-dates:focus {
  color: var(--marketplaceColorDark, #e0691a) !important;
}
/* Legacy wrap/slot/absolute anchors from prior desktop attempts — neutralize. */
#${SEATICS_MAP_CONTAINER_ID} .promotix-see-other-dates-wrap,
#${SEATICS_MAP_CONTAINER_ID} .promotix-see-other-dates-slot {
  display: contents !important;
}
#${SEATICS_MAP_CONTAINER_ID} .promotix-date-box-anchor,
#${SEATICS_MAP_CONTAINER_ID} #event-info-area.promotix-event-info-area-anchor {
  overflow: visible !important;
  height: auto !important;
  max-height: none !important;
}
/* Hide TN's live text (flashes "Checkout" on qty change); show our label via ::after. */
#${SEATICS_MAP_CONTAINER_ID} #pre-checkout-price-cta,
#sea-mp #pre-checkout-price-cta,
body[${SEATICS_EMBED_BODY_ATTR}="1"] #sea-mp #pre-checkout-price-cta,
#pre-checkout-price-cta {
  background-color: var(--marketplaceColor) !important;
  border-color: var(--marketplaceColor) !important;
  color: transparent !important;
  font-size: 0 !important;
  line-height: 0 !important;
}
#${SEATICS_MAP_CONTAINER_ID} #pre-checkout-price-cta::after,
#sea-mp #pre-checkout-price-cta::after,
body[${SEATICS_EMBED_BODY_ATTR}="1"] #sea-mp #pre-checkout-price-cta::after,
#pre-checkout-price-cta::after {
  content: 'Go to Secure Checkout';
  display: inline-block !important;
  font-size: 14px !important;
  line-height: 1.25 !important;
  font-weight: 700 !important;
  color: var(--colorWhite, #fff) !important;
  white-space: normal !important;
}
#${SEATICS_MAP_CONTAINER_ID} #pre-checkout-price-cta:hover,
#${SEATICS_MAP_CONTAINER_ID} #pre-checkout-price-cta:focus,
#sea-mp #pre-checkout-price-cta:hover,
#sea-mp #pre-checkout-price-cta:focus,
body[${SEATICS_EMBED_BODY_ATTR}="1"] #sea-mp #pre-checkout-price-cta:hover,
body[${SEATICS_EMBED_BODY_ATTR}="1"] #sea-mp #pre-checkout-price-cta:focus,
#pre-checkout-price-cta:hover,
#pre-checkout-price-cta:focus {
  background-color: var(--marketplaceColorDark) !important;
  border-color: var(--marketplaceColorDark) !important;
  color: transparent !important;
}
#${SEATICS_MAP_CONTAINER_ID} .venue-ticket-list-row-js,
body[${SEATICS_EMBED_BODY_ATTR}="1"] #sea-mp .venue-ticket-list-row-js {
  cursor: pointer !important;
}
/* Ticket list rows — let section column take remaining width; price + CTA shrink to content.
   table-layout:fixed + width:34% on CTA crushed the section cell on some venues (4-column rows). */
#${SEATICS_MAP_CONTAINER_ID} table[class*="venue-ticket-list"],
body[${SEATICS_EMBED_BODY_ATTR}="1"] #sea-mp table[class*="venue-ticket-list"] {
  width: 100% !important;
}
#${SEATICS_MAP_CONTAINER_ID} tr.venue-ticket-list-row-js > td:not(:nth-last-child(-n+2)),
body[${SEATICS_EMBED_BODY_ATTR}="1"] #sea-mp tr.venue-ticket-list-row-js > td:not(:nth-last-child(-n+2)) {
  vertical-align: middle !important;
  word-break: normal !important;
  overflow-wrap: normal !important;
  white-space: normal !important;
}
#${SEATICS_MAP_CONTAINER_ID} tr.venue-ticket-list-row-js > td:nth-last-child(2),
body[${SEATICS_EMBED_BODY_ATTR}="1"] #sea-mp tr.venue-ticket-list-row-js > td:nth-last-child(2) {
  vertical-align: middle !important;
  text-align: right !important;
  width: 1% !important;
  white-space: nowrap !important;
  padding: 4px 8px 4px 4px !important;
}
#${SEATICS_MAP_CONTAINER_ID} td.venue-ticket-list-cta-col,
#${SEATICS_MAP_CONTAINER_ID} td.venue-ticket-list-cta-js,
body[${SEATICS_EMBED_BODY_ATTR}="1"] #sea-mp td.venue-ticket-list-cta-col,
body[${SEATICS_EMBED_BODY_ATTR}="1"] #sea-mp td.venue-ticket-list-cta-js {
  vertical-align: middle !important;
  text-align: center !important;
  width: 1% !important;
  white-space: nowrap !important;
  padding: 4px 6px 4px 4px !important;
}
/* Native TN per-row CTA button (inside buyButtonContentHtml). */
#${SEATICS_MAP_CONTAINER_ID} .venue-ticket-list-cta-button,
#${SEATICS_MAP_CONTAINER_ID} .promotix-seatics-buy-cta,
body[${SEATICS_EMBED_BODY_ATTR}="1"] #sea-mp .venue-ticket-list-cta-button,
body[${SEATICS_EMBED_BODY_ATTR}="1"] #sea-mp .promotix-seatics-buy-cta {
  display: inline-block !important;
  box-sizing: border-box !important;
  width: auto !important;
  max-width: none !important;
  margin: 0 !important;
  padding: 7px 10px !important;
  font-size: 12px !important;
  font-weight: 700 !important;
  line-height: 1.25 !important;
  border-radius: 4px !important;
  white-space: nowrap !important;
  text-align: center !important;
  background-color: var(--marketplaceColor, #ff822f) !important;
  border: 1px solid var(--marketplaceColor, #ff822f) !important;
  color: var(--colorWhite, #fff) !important;
  cursor: pointer !important;
}
#${SEATICS_MAP_CONTAINER_ID} .venue-ticket-list-cta-button:hover,
#${SEATICS_MAP_CONTAINER_ID} .venue-ticket-list-cta-button:focus,
#${SEATICS_MAP_CONTAINER_ID} .promotix-seatics-buy-cta:hover,
#${SEATICS_MAP_CONTAINER_ID} .promotix-seatics-buy-cta:focus,
body[${SEATICS_EMBED_BODY_ATTR}="1"] #sea-mp .venue-ticket-list-cta-button:hover,
body[${SEATICS_EMBED_BODY_ATTR}="1"] #sea-mp .venue-ticket-list-cta-button:focus,
body[${SEATICS_EMBED_BODY_ATTR}="1"] #sea-mp .promotix-seatics-buy-cta:hover,
body[${SEATICS_EMBED_BODY_ATTR}="1"] #sea-mp .promotix-seatics-buy-cta:focus {
  background-color: var(--marketplaceColorDark, #e0691a) !important;
  border-color: var(--marketplaceColorDark, #e0691a) !important;
  color: var(--colorWhite, #fff) !important;
}
@media (max-width: 991px) {
  #${SEATICS_MAP_CONTAINER_ID} .event-info-details-ctn,
  #${SEATICS_MAP_CONTAINER_ID} .event-info-right-col {
    width: 100% !important;
    max-width: 100% !important;
    flex: 1 1 auto !important;
  }
  #${SEATICS_MAP_CONTAINER_ID} .event-info-details-ctn .event-info-content,
  #${SEATICS_MAP_CONTAINER_ID} .event-info-details-ctn .event-info-place,
  #${SEATICS_MAP_CONTAINER_ID} .event-info-details-ctn .event-info-date-time-span,
  #${SEATICS_MAP_CONTAINER_ID} .event-info-details-ctn .event-info-name {
    white-space: normal !important;
    overflow: visible !important;
    text-overflow: clip !important;
    overflow-wrap: break-word !important;
    word-break: break-word !important;
  }
  /* Strategy B: native mobile Seatics stays on body #sea-mp.
   * Do NOT move it into #tn-maps (that expanded Show Map into the desktop band).
   *
   * Native: in-flow banner; do not force #list-ctn top.
   * Safari: banner is in-flow+sticky under the topbar; JS pins fixed Seatics
   * chrome to --listingSeaticsStackTop (topbar + 36). Padding here is a floor
   * for in-flow title nodes — never smaller than that stack.
   */
  body[${SEATICS_EMBED_BODY_ATTR}="1"] #sea-mp {
    display: block !important;
    visibility: visible !important;
    box-sizing: border-box !important;
    padding-top: calc(
      var(
        --listingSeaticsStackTop,
        calc(var(--topbarHeight, 60px) + var(--listingRewardsBannerHeight, 0px))
      ) + 8px
    ) !important;
  }
  html[${NATIVE_WEBVIEW_HTML_ATTR}='1'] body[${SEATICS_EMBED_BODY_ATTR}="1"] #sea-mp {
    padding-top: calc(var(--topbarHeight, 60px) + 8px) !important;
  }
  body[${SEATICS_EMBED_BODY_ATTR}="1"] #sea-mp #sea-event-info-name,
  body[${SEATICS_EMBED_BODY_ATTR}="1"] #sea-mp .event-info-name {
    margin-top: 0 !important;
    padding-top: 2px !important;
  }
  /* Desktop chrome forced on a narrow viewport: show the map host only. */
  body[${SEATICS_EMBED_BODY_ATTR}="1"] #${SEATICS_MAP_CONTAINER_ID} {
    visibility: visible !important;
  }
  body[${SEATICS_EMBED_BODY_ATTR}="1"] #${SEATICS_MAP_CONTAINER_ID} #event-info-area {
    position: relative !important;
    z-index: auto !important;
    width: 100% !important;
  }
  body[${SEATICS_EMBED_BODY_ATTR}="1"] #${SEATICS_MAP_CONTAINER_ID} #tn-maps-inner {
    height: 100% !important;
    min-height: 100% !important;
  }
  #${SEATICS_MAP_CONTAINER_ID} .venue-ticket-list-cta-button,
  #${SEATICS_MAP_CONTAINER_ID} .promotix-seatics-buy-cta,
  body[${SEATICS_EMBED_BODY_ATTR}="1"] #sea-mp .venue-ticket-list-cta-button,
  body[${SEATICS_EMBED_BODY_ATTR}="1"] #sea-mp .promotix-seatics-buy-cta {
    padding: 6px 8px !important;
    font-size: 11px !important;
  }
  /*
   * Production drawer layout is unchanged. WKWebView only: ticket rows sit on a
   * higher compositor layer than Seatics' overlay because .seaticsViewport uses
   * translateZ(0). Flatten those layers while the quantity sheet is open.
   * Do not restyle the sheet, scrim, close control, or quantity circles.
   */
  body[${PRECHECKOUT_OPEN_BODY_ATTR}="1"] #list-ctn .venue-ticket-list-row-js,
  body[${PRECHECKOUT_OPEN_BODY_ATTR}="1"] #sea-mp .venue-ticket-list-row-js,
  body[${PRECHECKOUT_OPEN_BODY_ATTR}="1"] #list-ctn .venue-ticket-list-cta-button,
  body[${PRECHECKOUT_OPEN_BODY_ATTR}="1"] #list-ctn .promotix-seatics-buy-cta,
  body[${PRECHECKOUT_OPEN_BODY_ATTR}="1"] #sea-mp .venue-ticket-list-cta-button,
  body[${PRECHECKOUT_OPEN_BODY_ATTR}="1"] #sea-mp .promotix-seatics-buy-cta {
    transform: none !important;
    -webkit-transform: none !important;
    will-change: auto !important;
    isolation: auto !important;
    z-index: 0 !important;
  }
  body[${PRECHECKOUT_OPEN_BODY_ATTR}="1"] #hubspot-messages-iframe-container,
  body[${PRECHECKOUT_OPEN_BODY_ATTR}="1"] #hubspot-messages-iframe-container iframe,
  body[${PRECHECKOUT_OPEN_BODY_ATTR}="1"] .hs-shadow-container,
  body[${PRECHECKOUT_OPEN_BODY_ATTR}="1"] iframe#hubspot-conversations-iframe {
    visibility: hidden !important;
    pointer-events: none !important;
  }
  /*
   * Attendee native WebView runs the SAME Seatics mobile layout as Safari.
   * Do NOT add overflow / height / position rules for #list-ctn or #sea-mp here:
   * Seatics' quantity sheet is a descendant of #list-ctn and only escapes the
   * list box because that box has no clip or height cap. Constraining it traps
   * the sheet inside the ticket band (circles + close control clip at the Show
   * Map bar, sheet crops horizontally, sheet scrolls with the list).
   *
   * WKWebView keeps ticket rows on their own compositor layers, so they paint
   * over the bottom of the drawer even after the flatten rule above. Suppress
   * their paint outright. Visibility is deliberate: it stops a promoted layer
   * from painting while leaving layout identical, and it is overridable per
   * element, so nothing inside the sheet card is affected.
   */
  html[${NATIVE_WEBVIEW_HTML_ATTR}="1"] body[${PRECHECKOUT_OPEN_BODY_ATTR}="1"] [${NATIVE_SHEET_BLEED_ATTR}="1"] {
    visibility: hidden !important;
  }
}
@media (min-width: 992px) {
  #${SEATICS_MAP_CONTAINER_ID} .event-info-details-ctn {
    width: 25% !important;
    max-width: 25% !important;
    flex: 0 0 25% !important;
  }
  #${SEATICS_MAP_CONTAINER_ID} .event-info-right-col {
    width: 628px !important;
    max-width: 628px !important;
    flex: 0 0 628px !important;
  }
  #${SEATICS_MAP_CONTAINER_ID} .event-info-details-ctn .event-info-content,
  #${SEATICS_MAP_CONTAINER_ID} .event-info-details-ctn .event-info-place,
  #${SEATICS_MAP_CONTAINER_ID} .event-info-details-ctn .event-info-date-time-span,
  #${SEATICS_MAP_CONTAINER_ID} .event-info-details-ctn .event-info-name {
    white-space: normal !important;
    overflow: visible !important;
    text-overflow: clip !important;
    overflow-wrap: anywhere !important;
    word-break: break-word !important;
  }
  #${SEATICS_MAP_CONTAINER_ID} .event-info-details-ctn .event-info-place span,
  #${SEATICS_MAP_CONTAINER_ID} .event-info-details-ctn .event-info-date-time-span span,
  #${SEATICS_MAP_CONTAINER_ID} .event-info-details-ctn #sea-event-info-name {
    display: inline !important;
    white-space: normal !important;
    overflow-wrap: anywhere !important;
  }
}
${LOWEST_PRICE_PILL_CSS}
${TICKET_PACKAGES_PROMO_CSS}
`;
};

/**
 * Seatics renders inconsistent ticket subtitle copy across events (e.g. Ticket Price + Fee + Taxes,
 * Fees Included, etc). Normalize to PromoTix wording so inventory rows are consistent.
 */
const installTicketPriceBreakdownPatch = cancelled => {
  if (typeof document === 'undefined') return () => {};
  const ticketPriceLineRe =
    /Ticket Price\s*\$([0-9.,]+)(?:\s*\+\s*Fee\s*\$[0-9.,]+)?(?:\s*\+\s*Tax(?:es|is|i?s)(?:\s*if\s*applicable)?)?/gi;
  const feesIncludedRe = /\bFees Included\b/gi;

  const patch = () => {
    if (cancelled()) return;
    const root = document.getElementById(SEATICS_MAP_CONTAINER_ID);
    if (!root) return;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    let textNode = walker.nextNode();
    while (textNode) {
      const currentNode = textNode;
      textNode = walker.nextNode();
      const raw = currentNode.nodeValue || '';
      if (!/Ticket Price\s*\$|Fees Included/i.test(raw)) continue;
      let replaced = raw.replace(ticketPriceLineRe, (_full, ticketPart) => `Total Price $${ticketPart}`);
      replaced = replaced.replace(feesIncludedRe, 'Total Price includes fees');
      if (replaced !== raw) {
        currentNode.nodeValue = replaced;
      }
    }
  };

  const observerRoot = document.getElementById(SEATICS_MAP_CONTAINER_ID) || document.body;
  let patchScheduled = false;
  const schedulePatch = () => {
    if (patchScheduled || cancelled()) return;
    patchScheduled = true;
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => {
        patchScheduled = false;
        patch();
      });
    } else {
      setTimeout(() => {
        patchScheduled = false;
        patch();
      }, 0);
    }
  };
  const observer = new MutationObserver(() => schedulePatch());
  observer.observe(observerRoot, { childList: true, subtree: true });
  const interval = setInterval(patch, 2000);
  patch();
  return () => {
    observer.disconnect();
    clearInterval(interval);
  };
};

/**
 * Seatics sometimes renders duplicated filter chips/rows (Tickets/ADA/Filters).
 * Keep this conservative: only remove clear same-parent duplicates. Aggressive
 * whole-bar hiding previously removed the real Tickets/ADA controls on mobile.
 */
const installFilterDedupePatch = cancelled => {
  if (typeof document === 'undefined') return () => {};

  const norm = s =>
    String(s || '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  const isFilterLabel = txt =>
    /^(tickets|ada accessible|parking passes|filters(?:\s*\(\d+\))?)$/i.test(String(txt || '').trim());

  const filterChromeRoots = () =>
    ['sea-mp', SEATICS_MAP_CONTAINER_ID]
      .map(id => document.getElementById(id))
      .filter((el, i, arr) => el && arr.indexOf(el) === i);

  const patchRoot = root => {
    if (!(root instanceof Element)) return;

    // Undo prior over-aggressive hides from style v25 so a hard refresh isn't required
    // for every wrongly-hidden real tab after deploy.
    root.querySelectorAll('[data-promotix-filter-deduped="1"]').forEach(el => {
      if (!(el instanceof HTMLElement)) return;
      el.style.removeProperty('display');
      el.removeAttribute('data-promotix-filter-deduped');
    });

    // 1) Remove duplicate chips inside each potential filter row.
    const rows = root.querySelectorAll('div, nav, ul');
    rows.forEach(row => {
      if (!(row instanceof HTMLElement)) return;
      const children = Array.from(row.children || []);
      if (children.length < 2 || children.length > 10) return;
      const labels = children.map(c => (c.textContent || '').replace(/\s+/g, ' ').trim());
      if (!labels.some(isFilterLabel)) return;
      if (!labels.every(t => !t || isFilterLabel(t))) return;

      const seen = new Set();
      children.forEach(c => {
        const t = norm(c.textContent || '');
        if (!isFilterLabel(t)) return;
        if (seen.has(t)) {
          c.remove();
        } else {
          seen.add(t);
        }
      });
    });

    // 2) Remove duplicate whole rows with the same filter-label fingerprint.
    const byParent = new WeakMap();
    rows.forEach(row => {
      if (!(row instanceof HTMLElement)) return;
      const children = Array.from(row.children || []);
      if (children.length < 2 || children.length > 10) return;
      const labels = children.map(c => (c.textContent || '').replace(/\s+/g, ' ').trim());
      if (!labels.some(isFilterLabel)) return;
      if (!labels.every(t => !t || isFilterLabel(t))) return;
      const fingerprint = labels.map(norm).filter(Boolean).join('|');
      if (!fingerprint) return;
      const parent = row.parentElement;
      if (!parent) return;
      const seenInParent = byParent.get(parent) || new Set();
      if (seenInParent.has(fingerprint)) {
        row.remove();
      } else {
        seenInParent.add(fingerprint);
        byParent.set(parent, seenInParent);
      }
    });

    // 3) Hide only concatenated clone nodes (text repeats "tickets" … "tickets").
    Array.from(root.querySelectorAll('div, span, nav')).forEach(el => {
      if (!(el instanceof HTMLElement)) return;
      if (el.closest('table, tr, .venue-ticket-list-row-js')) return;
      const t = norm(el.textContent || '');
      if (!t || t.length > 90) return;
      const ticketsCount = (t.match(/\btickets\b/g) || []).length;
      if (ticketsCount < 2) return;
      if (!/\bada accessible\b/.test(t)) return;
      if (/\$|select tickets|buy tickets|general admission/.test(t)) return;
      // Prefer hiding leaf-ish clones, not large ancestors of the real bar.
      if (el.querySelector('button, a')) return;
      el.style.setProperty('display', 'none', 'important');
    });
  };

  const patch = () => {
    if (cancelled()) return;
    filterChromeRoots().forEach(patchRoot);
  };

  let patchScheduled = false;
  const schedulePatch = () => {
    if (patchScheduled || cancelled()) return;
    patchScheduled = true;
    const run = () => {
      patchScheduled = false;
      patch();
    };
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(run);
    } else {
      setTimeout(run, 0);
    }
  };
  const observer = new MutationObserver(() => schedulePatch());
  const observeTargets = filterChromeRoots();
  if (observeTargets.length) {
    observeTargets.forEach(el => observer.observe(el, { childList: true, subtree: true }));
  } else if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  }
  const bootObserver = new MutationObserver(() => {
    filterChromeRoots().forEach(el => {
      try {
        observer.observe(el, { childList: true, subtree: true });
      } catch (e) {
        // already observing
      }
    });
    schedulePatch();
  });
  if (document.body) {
    bootObserver.observe(document.body, { childList: true, subtree: true });
  }
  const interval = setInterval(patch, 1500);
  patch();
  return () => {
    observer.disconnect();
    bootObserver.disconnect();
    clearInterval(interval);
  };
};

/**
 * On mobile viewports, force Seatics into desktop rendering mode to avoid the
 * fullscreen position:fixed #sea-mp overlay that can't be reliably contained.
 * Uses Object.defineProperty (not Proxy) so Seatics' for..in / Object.keys()
 * still work normally -- only smallBreakpoint and startingInDesktopMode are locked.
 *
 * Must be called BEFORE the MapAndLayout script is appended to the DOM.
 * Returns a cleanup function that restores normal window.Seatics property behavior.
 */
const installDesktopModeForMobile = () => {
  if (typeof window === 'undefined') return () => {};
  if (window.innerWidth >= 992) return () => {};

  const lockConfigProps = cfg => {
    if (!cfg || typeof cfg !== 'object') return;
    try {
      Object.defineProperty(cfg, 'smallBreakpoint', {
        get: () => 0,
        set: () => {},
        configurable: true,
        enumerable: true,
      });
    } catch (e) { /* already defined or frozen */ }
    try {
      Object.defineProperty(cfg, 'startingInDesktopMode', {
        get: () => true,
        set: () => {},
        configurable: true,
        enumerable: true,
      });
    } catch (e) { /* already defined or frozen */ }
  };

  let _seatics = window.Seatics;

  Object.defineProperty(window, 'Seatics', {
    get() { return _seatics; },
    set(v) {
      _seatics = v;
      if (v && typeof v === 'object') {
        if (v.config) lockConfigProps(v.config);
        let _config = v.config;
        try {
          Object.defineProperty(v, 'config', {
            get() { return _config; },
            set(c) {
              _config = c;
              lockConfigProps(c);
            },
            configurable: true,
            enumerable: true,
          });
        } catch (e) { /* ignore */ }
      }
    },
    configurable: true,
    enumerable: true,
  });

  if (_seatics) {
    window.Seatics = _seatics;
  }

  return () => {
    try {
      const current = _seatics;
      delete window.Seatics;
      if (current !== undefined) window.Seatics = current;
    } catch (e) { /* ignore */ }
  };
};

/**
 * Mobile Safari: header → 5% banner → Seatics.
 *
 * Native WebView already has this stack (in-flow banner, Seatics left alone).
 * Safari's #sea-mp / #list-ctn are often position:fixed against the VIEWPORT, so
 * they ignore parent padding and sit under the banner unless we pin `top`.
 *
 * Stack top is always topbar + 36px — never a measured sticky-header height
 * (that is topbar-only once the banner is in-flow, which pinned Seatics through
 * the orange strip).
 */
const LISTING_SEATICS_TOP_GAP_PX = 8;
const LISTING_MOBILE_BANNER_PX = 36;
const LISTING_STICKY_CHROME_HEIGHT_VAR = '--listingStickyChromeHeight';
const LISTING_SEATICS_STACK_TOP_VAR = '--listingSeaticsStackTop';

const SEATICS_STACK_PIN_SELECTORS = [
  '#sea-mp',
  '#list-ctn',
  '#event-info-area',
  '.event-info-details-ctn',
  '#sea-event-info-name',
  '.event-info-name',
];

const listingTopbarPx = () => {
  const style = window.getComputedStyle(document.documentElement);
  return parseFloat((style.getPropertyValue('--topbarHeight') || '').trim()) || 60;
};

/** Floor for Safari: never pin Seatics higher than below the 36px banner. */
const listingSafariSeaticsStackTopPx = () => listingTopbarPx() + LISTING_MOBILE_BANNER_PX;

const listingSeaticsTopClearancePx = () => {
  if (isNativeWebViewMarked()) {
    return listingTopbarPx() + LISTING_SEATICS_TOP_GAP_PX;
  }
  return listingSafariSeaticsStackTopPx() + LISTING_SEATICS_TOP_GAP_PX;
};

const pinFixedSeaticsNode = (el, stackTop) => {
  if (!(el instanceof HTMLElement)) return;
  const pos = window.getComputedStyle(el).position;
  if (pos !== 'fixed' && pos !== 'absolute') return;
  const top = el.getBoundingClientRect().top;
  if (!Number.isFinite(top)) return;
  if (top >= stackTop - 1) return;
  el.style.setProperty('top', `${stackTop}px`, 'important');
};

const syncSeaticsStickyClearance = () => {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  if (window.innerWidth >= 992 || !isNativeWebViewMarked()) return;

  const seaMp = document.getElementById('sea-mp');
  if (!(seaMp instanceof HTMLElement)) return;

  // Native app layout is already correct — do not fight Seatics' top.
  if (isNativeWebViewMarked()) {
    seaMp.style.setProperty(
      'padding-top',
      `${listingSeaticsTopClearancePx()}px`,
      'important'
    );
    seaMp.style.setProperty('box-sizing', 'border-box', 'important');
    return;
  }

  const stackTop = listingSafariSeaticsStackTopPx();
  document.documentElement.style.setProperty(LISTING_SEATICS_STACK_TOP_VAR, `${stackTop}px`);
  document.documentElement.style.setProperty(LISTING_STICKY_CHROME_HEIGHT_VAR, `${stackTop}px`);

  seaMp.style.setProperty('padding-top', `${stackTop + LISTING_SEATICS_TOP_GAP_PX}px`, 'important');
  seaMp.style.setProperty('box-sizing', 'border-box', 'important');

  const seen = new Set();
  SEATICS_STACK_PIN_SELECTORS.forEach(sel => {
    document.querySelectorAll(sel).forEach(el => {
      if (seen.has(el)) return;
      seen.add(el);
      pinFixedSeaticsNode(el, stackTop);
    });
  });
};

const installSeaticsStickyClearanceSync = cancelled => {
  if (typeof document === 'undefined' || typeof window === 'undefined') return () => {};
  // Mobile browser listings now scroll header, banner and event title together.
  // Do not pin vendor descendants to viewport coordinates (or disable browser
  // scroll restoration). Preserve the existing native and desktop paths.
  if (window.innerWidth < 992 && !isNativeWebViewMarked()) return () => {};

  const prevScrollRestoration = window.history?.scrollRestoration;
  try {
    if (window.innerWidth < 992 && window.history && 'scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  } catch (e) {
    /* ignore */
  }

  let rafFollow = 0;
  let rafFollowUntil = 0;
  const followSeaticsLayout = () => {
    rafFollow = 0;
    if (cancelled()) return;
    syncSeaticsStickyClearance();
    if (Date.now() < rafFollowUntil) {
      rafFollow = window.requestAnimationFrame(followSeaticsLayout);
    }
  };
  const kickFollow = (ms = 2000) => {
    rafFollowUntil = Math.max(rafFollowUntil, Date.now() + ms);
    if (!rafFollow) rafFollow = window.requestAnimationFrame(followSeaticsLayout);
  };

  const run = () => {
    if (cancelled()) return;
    syncSeaticsStickyClearance();
  };

  run();
  kickFollow(2500);

  let debounce = null;
  const schedule = () => {
    if (cancelled()) return;
    if (debounce) clearTimeout(debounce);
    debounce = setTimeout(() => {
      run();
      kickFollow(1500);
    }, 16);
  };

  let observer = null;
  if (typeof MutationObserver !== 'undefined') {
    observer = new MutationObserver(() => {
      if (cancelled()) return;
      run();
      kickFollow(1500);
    });
    observer.observe(document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'id'],
    });
  }

  window.addEventListener('resize', schedule);
  window.addEventListener('orientationchange', schedule);
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', schedule);
  }

  return () => {
    if (rafFollow) window.cancelAnimationFrame(rafFollow);
    if (debounce) clearTimeout(debounce);
    if (observer) observer.disconnect();
    window.removeEventListener('resize', schedule);
    window.removeEventListener('orientationchange', schedule);
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', schedule);
    }
    try {
      if (prevScrollRestoration && window.history && 'scrollRestoration' in window.history) {
        window.history.scrollRestoration = prevScrollRestoration;
      }
    } catch (e) {
      /* ignore */
    }
  };
};

/**
 * Seatics can re-enable fullscreen mobile overlay mode after initial render.
 * Force embedded behavior: keep map container in-flow and prevent body scroll lock.
 */
const installSeaticsMobileEmbedGuard = cancelled => {
  if (typeof document === 'undefined' || typeof window === 'undefined') return () => {};
  const isSeaticsNode = el => {
    if (!(el instanceof HTMLElement)) return false;
    const id = String(el.id || '').toLowerCase();
    const cls = String(el.className || '').toLowerCase();
    return id.includes('sea') || id.includes('seatics') || cls.includes('sea-') || cls.includes('seatics');
  };

  const apply = () => {
    if (cancelled()) return;
    const mapsRoot = document.getElementById(SEATICS_MAP_CONTAINER_ID);
    const seaMp = document.getElementById('sea-mp');

    // Keep Seatics mobile container inside our listing map host (not fixed viewport layer on body).
    if (mapsRoot && seaMp && !mapsRoot.contains(seaMp)) {
      mapsRoot.appendChild(seaMp);
    }

    if (seaMp instanceof HTMLElement) {
      seaMp.style.setProperty('display', 'block', 'important');
      seaMp.style.setProperty('position', 'relative', 'important');
      seaMp.style.setProperty('inset', 'auto', 'important');
      seaMp.style.setProperty('z-index', 'auto', 'important');
      seaMp.style.setProperty('width', '100%', 'important');
      seaMp.style.setProperty('height', 'auto', 'important');
      seaMp.style.setProperty('max-height', 'none', 'important');
      seaMp.style.setProperty('overflow', 'visible', 'important');
      seaMp.style.setProperty('margin', '0', 'important');
      // Keep top padding so the event title clears sticky PromoTix topbar + rewards banner.
      // (A blanket padding:0 here was clipping the title until rubber-band pull.)
      // Amount comes from reserved CSS vars only (CLS-safe; banner height is locked).
      seaMp.style.setProperty('padding', `${listingSeaticsTopClearancePx()}px 0 0 0`, 'important');
      seaMp.style.setProperty('box-sizing', 'border-box', 'important');
      seaMp.style.setProperty('touch-action', 'auto', 'important');
    }

    // Seatics may inject fullscreen fixed descendants; pin them back into flow.
    if (seaMp instanceof HTMLElement) {
      let fixedCount = 0;
      const nodes = seaMp.querySelectorAll('*');
      for (let i = 0; i < nodes.length && fixedCount < 40; i += 1) {
        const el = nodes[i];
        if (!(el instanceof HTMLElement)) continue;
        const cs = window.getComputedStyle(el);
        if (cs.position !== 'fixed') continue;
        const r = el.getBoundingClientRect();
        if (r.width < window.innerWidth * 0.5 && r.height < window.innerHeight * 0.5) continue;
        el.style.setProperty('position', 'absolute', 'important');
        el.style.setProperty('inset', '0 auto auto 0', 'important');
        el.style.setProperty('width', '100%', 'important');
        el.style.setProperty('height', 'auto', 'important');
        el.style.setProperty('max-height', 'none', 'important');
        fixedCount += 1;
      }
    }

    // In forced-desktop mode on mobile, Seatics renders a position:fixed ticket
    // panel inside #tn-maps. The CSS transform containing-block trick handles most
    // cases, but belt-and-suspenders: also pin them via JS.
    if (mapsRoot instanceof HTMLElement) {
      const tnNodes = mapsRoot.querySelectorAll('*');
      let tnFixed = 0;
      for (let i = 0; i < tnNodes.length && tnFixed < 20; i += 1) {
        const el = tnNodes[i];
        if (!(el instanceof HTMLElement)) continue;
        const cs = window.getComputedStyle(el);
        if (cs.position !== 'fixed') continue;
        el.style.setProperty('position', 'absolute', 'important');
        tnFixed += 1;
      }
    }

    // Some mobile Seatics variants create fullscreen fixed layers on <body> (outside #sea-mp).
    // Detect large viewport-covering Seatics nodes and move/neutralize them.
    const viewportW = window.innerWidth || 0;
    const viewportH = window.innerHeight || 0;
    const allBodyNodes = document.body ? Array.from(document.body.querySelectorAll('*')) : [];
    let movedLayers = 0;
    for (let i = 0; i < allBodyNodes.length && movedLayers < 8; i += 1) {
      const el = allBodyNodes[i];
      if (!(el instanceof HTMLElement) || !isSeaticsNode(el)) continue;
      const cs = window.getComputedStyle(el);
      if (cs.position !== 'fixed') continue;
      const r = el.getBoundingClientRect();
      const coversViewport = r.width >= viewportW * 0.88 && r.height >= viewportH * 0.78;
      if (!coversViewport) continue;
      if (mapsRoot && !mapsRoot.contains(el)) {
        mapsRoot.appendChild(el);
      }
      el.style.setProperty('position', 'relative', 'important');
      el.style.setProperty('inset', 'auto', 'important');
      el.style.setProperty('top', 'auto', 'important');
      el.style.setProperty('left', 'auto', 'important');
      el.style.setProperty('right', 'auto', 'important');
      el.style.setProperty('bottom', 'auto', 'important');
      el.style.setProperty('width', '100%', 'important');
      el.style.setProperty('height', 'auto', 'important');
      el.style.setProperty('max-height', 'none', 'important');
      el.style.setProperty('overflow', 'visible', 'important');
      movedLayers += 1;
    }

    // Mobile overlay mode often locks scrolling to an internal Seatics pane.
    // Convert oversized internal scroll panes back to normal flow scroll.
    if (seaMp instanceof HTMLElement) {
      const scrollContainers = seaMp.querySelectorAll('div, section, main, article');
      let normalized = 0;
      for (let i = 0; i < scrollContainers.length && normalized < 24; i += 1) {
        const el = scrollContainers[i];
        if (!(el instanceof HTMLElement)) continue;
        const cs = window.getComputedStyle(el);
        const hasOwnScroll = /(auto|scroll)/i.test(cs.overflowY || '') || /(auto|scroll)/i.test(cs.overflow || '');
        if (!hasOwnScroll) continue;
        const r = el.getBoundingClientRect();
        if (r.height < viewportH * 0.55) continue;
        el.style.setProperty('overflow', 'visible', 'important');
        el.style.setProperty('overflow-y', 'visible', 'important');
        el.style.setProperty('max-height', 'none', 'important');
        el.style.setProperty('height', 'auto', 'important');
        normalized += 1;
      }
    }

    const backdrop = document.querySelector('.sea-dialog-background');
    if (backdrop instanceof HTMLElement) {
      backdrop.style.setProperty('display', 'none', 'important');
    }

    if (document.body) {
      document.body.style.setProperty('overflow', 'auto', 'important');
      document.body.style.setProperty('overflow-y', 'auto', 'important');
      document.body.style.setProperty('position', 'static', 'important');
      document.body.style.setProperty('touch-action', 'auto', 'important');
      document.body.style.setProperty('overscroll-behavior', 'auto', 'important');
      document.body.style.removeProperty('height');
      document.body.style.removeProperty('top');
      document.body.style.removeProperty('left');
      document.body.style.removeProperty('right');
      document.body.style.removeProperty('width');
      document.body.classList.forEach(c => {
        if (/(seatics|sea-|no-scroll|noscroll|scroll-lock|modal-open)/i.test(c)) {
          document.body.classList.remove(c);
        }
      });
    }
    if (document.documentElement) {
      document.documentElement.style.setProperty('overflow', 'auto', 'important');
      document.documentElement.style.setProperty('overflow-y', 'auto', 'important');
      document.documentElement.style.setProperty('position', 'static', 'important');
      document.documentElement.style.setProperty('touch-action', 'auto', 'important');
      document.documentElement.style.setProperty('overscroll-behavior', 'auto', 'important');
      document.documentElement.style.removeProperty('height');
      document.documentElement.classList.forEach(c => {
        if (/(seatics|sea-|no-scroll|noscroll|scroll-lock|modal-open)/i.test(c)) {
          document.documentElement.classList.remove(c);
        }
      });
    }
  };

  let patchScheduled = false;
  const scheduleApply = () => {
    if (patchScheduled || cancelled()) return;
    patchScheduled = true;
    const run = () => {
      patchScheduled = false;
      apply();
    };
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(run);
    } else {
      setTimeout(run, 0);
    }
  };

  const observerRoot = document.body || document.documentElement;
  const observer = new MutationObserver(() => scheduleApply());
  if (observerRoot) {
    observer.observe(observerRoot, { childList: true, subtree: true, attributes: true });
  }
  const interval = setInterval(apply, 800);
  apply();

  return () => {
    observer.disconnect();
    clearInterval(interval);
    if (document.body) {
      document.body.style.removeProperty('overflow');
      document.body.style.removeProperty('overflow-y');
      document.body.style.removeProperty('position');
      document.body.style.removeProperty('touch-action');
      document.body.style.removeProperty('overscroll-behavior');
    }
    if (document.documentElement) {
      document.documentElement.style.removeProperty('overflow');
      document.documentElement.style.removeProperty('overflow-y');
      document.documentElement.style.removeProperty('position');
      document.documentElement.style.removeProperty('touch-action');
      document.documentElement.style.removeProperty('overscroll-behavior');
    }
  };
};

/** Replace SonicSeats branding in Seatics.Text (recursive, depth-capped). */
const scrubSonicSeatsFromSeaticsText = (root, depth = 0) => {
  if (!root || typeof root !== 'object' || depth > 8) return;
  Object.keys(root).forEach(k => {
    const v = root[k];
    if (typeof v === 'string' && v.includes('SonicSeats')) {
      root[k] = v.split('SonicSeats').join('PromoTix');
    } else if (v && typeof v === 'object') {
      scrubSonicSeatsFromSeaticsText(v, depth + 1);
    }
  });
};

/** Replace TN default mobile resale blurb inside `Seatics.Text` strings (recursive, depth-capped). */
const scrubLegacySeaticsResaleDisclaimerInText = (root, depth = 0) => {
  if (!root || typeof root !== 'object' || depth > 8) return;
  Object.keys(root).forEach(k => {
    const v = root[k];
    if (typeof v === 'string') {
      const next = applySeaticsLegacyResaleSnippetReplacements(v);
      if (next !== v) {
        root[k] = next;
      }
    } else if (v && typeof v === 'object') {
      scrubLegacySeaticsResaleDisclaimerInText(v, depth + 1);
    }
  });
};

/**
 * Guarantee backdrop + panel use position:fixed. The Seatics map column uses a transformed
 * ancestor (see ListingPage.module.css .seaticsViewport), which makes fixed descendants lay out
 * relative to that box — centered in the map column, clipped, close control off-screen.
 * Port layers to document.body on open so fixed positioning uses the viewport.
 */
const removePortaledGuaranteeLayers = () => {
  if (typeof document === 'undefined') return;
  ['event-info-guarantee-backdrop', 'event-info-guarantee'].forEach(id => {
    const n = document.getElementById(id);
    if (n && n.parentNode === document.body) {
      n.remove();
    }
  });
};

const portGuaranteeModalLayersToBody = () => {
  if (typeof document === 'undefined' || !document.body) return;
  const backdrop = document.getElementById('event-info-guarantee-backdrop');
  const panel = document.getElementById('event-info-guarantee');
  if (!backdrop || !panel) return;
  if (backdrop.parentNode === document.body && panel.parentNode === document.body) {
    return;
  }
  document.body.appendChild(backdrop);
  document.body.appendChild(panel);
};

/** Seatics chrome can live under `#tn-maps` and/or body-level `#sea-mp` (native mobile). */
const getSeaticsChromeRoots = () => {
  if (typeof document === 'undefined') return [];
  const roots = [SEATICS_MAP_CONTAINER_ID, 'sea-mp', `${SEATICS_MAP_CONTAINER_ID}-inner`, 'tn-maps-inner']
    .map(id => document.getElementById(id))
    .filter(Boolean);
  // Dedupe nested roots (prefer outermost unique nodes).
  return roots.filter((el, i, arr) => !arr.some((other, j) => j !== i && other.contains(el)));
};

const looksLikeCurrentMobileTrustCopy = text => {
  const t = String(text || '').replace(/\s+/g, ' ').trim();
  return (
    t.includes('100% Money-Back Guarantee') &&
    t.includes('Final prices below') &&
    t.includes('verified marketplace')
  );
};

const looksLikeLegacyMobileResaleBlurb = text => {
  const t = String(text || '').replace(/\s+/g, ' ').trim();
  if (!t || t.length > 420) return false;
  if (looksLikeCurrentMobileTrustCopy(t) || t.includes(SEATICS_MOBILE_TRUST_HEADLINE)) return false;
  return (
    SEATICS_LEGACY_MOBILE_RESALE_SNIPPETS.some(s => t.includes(s)) ||
    (/resale marketplace/i.test(t) && /face value/i.test(t) && /not the venue/i.test(t))
  );
};

const looksLikeLegacyLongDisclosure = text => {
  const t = String(text || '').replace(/\s+/g, ' ').trim();
  if (!t) return false;
  if (looksLikeCurrentMobileTrustCopy(t) || t.includes(SEATICS_MOBILE_TRUST_HEADLINE)) return false;
  return (
    text.includes(SEATICS_DISCLOSURE_BODY) ||
    SEATICS_LEGACY_DISCLOSURE_SNIPPETS.some(s => t.includes(s)) ||
    (/SonicSeats/i.test(t) && /face value/i.test(t))
  );
};

/** Find the tightest element whose text is mostly the legacy blurb (not a huge parent). */
const findLegacyBlurbHosts = root => {
  if (!(root instanceof Element)) return [];
  const hosts = [];
  const all = root.querySelectorAll('div, p, span, section, aside, li');
  all.forEach(el => {
    if (!(el instanceof HTMLElement)) return;
    if (isOurMobileTrustChrome(el)) return;
    if (el.getAttribute(PROMOTIX_MOBILE_TRUST_ATTR) === '1') return;
    if (el.querySelector(`#${PROMOTIX_MOBILE_TRUST_MOUNT_ID}, [${PROMOTIX_MOBILE_TRUST_ATTR}="1"]`)) {
      return;
    }
    const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
    if (!looksLikeLegacyMobileResaleBlurb(text) && !looksLikeLegacyLongDisclosure(text)) {
      // Truncated TN flash of our scrubbed plain string (headline only / cut off).
      const looksLikeTruncatedTrust =
        text.includes('100% Money-Back Guarantee') &&
        text.length < 280 &&
        !text.includes('verified marketplace');
      if (!looksLikeTruncatedTrust) return;
    }
    // Prefer leaf-ish hosts: skip if a child already matches the same blurb alone.
    const childMatch = Array.from(el.children).some(child => {
      if (!(child instanceof HTMLElement) || isOurMobileTrustChrome(child)) return false;
      const ct = (child.textContent || '').replace(/\s+/g, ' ').trim();
      return (
        looksLikeLegacyMobileResaleBlurb(ct) ||
        looksLikeLegacyLongDisclosure(ct) ||
        (ct.includes('100% Money-Back Guarantee') && ct.length < 280)
      );
    });
    if (childMatch) return;
    if (text.length > 520) return;
    hosts.push(el);
  });
  return hosts;
};

const looksLikeInfoOnlyControl = el => {
  if (!(el instanceof HTMLElement)) return false;
  const label = `${el.getAttribute('aria-label') || ''} ${el.className || ''} ${el.id || ''} ${(el.textContent || '').trim()}`;
  return (
    /info|disclaimer|guarantee/i.test(label) ||
    /^[iⓘℹ!]$/i.test((el.textContent || '').trim()) ||
    /icon-info|info-icon|sea-info|icon-help|exclaim/i.test(String(el.className || ''))
  );
};

/** TN often leaves an ⓘ-only shell under Show Map after the blurb text is moved/removed. */
const findEmptyInfoDisclaimerShells = root => {
  if (!(root instanceof Element)) return [];
  const found = [];
  const seen = new Set();
  const push = el => {
    if (!(el instanceof HTMLElement) || seen.has(el)) return;
    if (isOurMobileTrustChrome(el)) return;
    if (el.querySelector(`#${PROMOTIX_MOBILE_TRUST_MOUNT_ID}`)) return;
    if (el.closest(`#${PROMOTIX_MOBILE_TRUST_MOUNT_ID}`)) return;
    if (el.closest('table, tr, .venue-ticket-list-row-js, nav, header')) return;
    seen.add(el);
    found.push(el);
  };

  const structured = root.querySelectorAll(
    `.event-info-disclaimer, [class*="disclaimer"], [class*="event-info-right"], [class*="event-info"]`
  );
  structured.forEach(el => {
    if (!(el instanceof HTMLElement)) return;
    const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
    const hasInfoChrome = hostKeepsInfoChrome(el) || !!el.querySelector('[class*="info"], i, svg');
    if ((hasInfoChrome && text.length < 16) || (!text && el.classList.contains('event-info-disclaimer'))) {
      push(el);
    }
  });

  root.querySelectorAll('button, [role="button"], a, span, i, div').forEach(el => {
    if (!(el instanceof HTMLElement) || !looksLikeInfoOnlyControl(el)) return;
    const parent = el.parentElement;
    if (!(parent instanceof HTMLElement)) return;
    const pt = (parent.textContent || '').replace(/\s+/g, ' ').trim();
    // Icon-only / nearly empty bar between Show Map and ticket filters.
    if (pt.length < 40) push(parent);
    else if ((el.textContent || '').replace(/\s+/g, ' ').trim().length < 3) push(el);
  });

  return found;
};

/** Hide the wasted TN ⓘ row once PromoTix trust copy is already visible elsewhere. */
const hideEmptyMobileInfoShells = root => {
  if (!(root instanceof Element)) return;
  const trustMount = root.querySelector(`#${PROMOTIX_MOBILE_TRUST_MOUNT_ID}`);
  findEmptyInfoDisclaimerShells(root).forEach(shell => {
    // Never hide our mount, its ancestors, or any node that still carries trust copy.
    if (trustMount && (shell === trustMount || shell.contains(trustMount) || trustMount.contains(shell))) {
      return;
    }
    if (
      shell.querySelector?.(
        `#${PROMOTIX_MOBILE_TRUST_MOUNT_ID}, .promotix-mobile-trust-headline, .promotix-mobile-trust-subcopy`
      )
    ) {
      return;
    }
    const text = (shell.textContent || '').replace(/\s+/g, ' ').trim();
    if (
      text.includes('Money-Back Guarantee') ||
      text.includes('verified marketplace') ||
      text.includes('Final prices below') ||
      text.length > 48
    ) {
      return;
    }
    shell.setAttribute(PROMOTIX_HIDE_INFO_SHELL_ATTR, '1');
    shell.style.setProperty('display', 'none', 'important');
    shell.style.setProperty('visibility', 'hidden', 'important');
    shell.style.setProperty('height', '0', 'important');
    shell.style.setProperty('max-height', '0', 'important');
    shell.style.setProperty('margin', '0', 'important');
    shell.style.setProperty('padding', '0', 'important');
    shell.style.setProperty('overflow', 'hidden', 'important');
    shell.setAttribute('aria-hidden', 'true');
  });
};

const replaceHostWithMobileTrustStrip = host => {
  if (!(host instanceof HTMLElement)) return;
  if (isOurMobileTrustChrome(host) && mobileTrustStripIsComplete(host)) return;
  placeMobileTrustMountInAnchor(host);
};

/** After layout paints, replace TN / legacy disclaimer markup with PromoTix copy. */
const patchDisclaimerDomIfNeeded = () => {
  if (typeof document === 'undefined') return;
  const roots = getSeaticsChromeRoots();
  if (!roots.length) return;

  roots.forEach(root => {
    // Repair our owned mount if TN emptied it; if it's complete and visible, just drop the ⓘ row.
    if (isSeaticsNativeMobileViewport()) {
      const mount = root.querySelector(`#${PROMOTIX_MOBILE_TRUST_MOUNT_ID}`);
      if (mount instanceof HTMLElement) {
        syncMobileTrustMountContent(mount);
        unclipMobileTrustAncestors(mount);
        if (mobileTrustStripIsComplete(mount)) {
          const rect = mount.getBoundingClientRect();
          // Prefer full text over a short clipped strip — keep unclipping until tall enough.
          if (rect.height >= 48 && rect.width >= 40) {
            hideEmptyMobileInfoShells(root);
            return;
          }
        }
      }
      // Trust copy may live in header via config HTML without our mount id — still hide ⓘ row
      // when the PromoTix trust lines are already visible in this root.
      const rootText = root.textContent || '';
      if (
        rootText.includes('100% Money-Back Guarantee') &&
        rootText.includes('verified marketplace')
      ) {
        hideEmptyMobileInfoShells(root);
      }
    }

    // 1) Prefer structured disclaimer nodes when present.
    root.querySelectorAll('.event-info-disclaimer').forEach(disc => {
      if (!(disc instanceof HTMLElement)) return;
      if (disc.id === PROMOTIX_MOBILE_TRUST_MOUNT_ID && mobileTrustStripIsComplete(disc)) return;
      if (disc.querySelector(`#${PROMOTIX_MOBILE_TRUST_MOUNT_ID}`) && mobileTrustStripIsComplete(disc)) {
        return;
      }
      if (disc.getAttribute(PROMOTIX_HIDE_INFO_SHELL_ATTR) === '1') return;
      const text = disc.textContent || '';

      if (isSeaticsNativeMobileViewport()) {
        const shouldPatchMobile =
          text.includes('100% Money-Back Guarantee') ||
          !text.trim() ||
          /SonicSeats/i.test(text) ||
          looksLikeLegacyMobileResaleBlurb(text) ||
          looksLikeLegacyLongDisclosure(text) ||
          /resale marketplace/i.test(text) ||
          /money[- ]?back guarantee/i.test(text) ||
          /face value/i.test(text);
        if (!shouldPatchMobile) return;
        // Empty/icon-only disclaimer under Show Map: hide it (copy already in header).
        if ((!text.trim() || text.length < 16) && hostKeepsInfoChrome(disc)) {
          hideEmptyMobileInfoShells(root);
          return;
        }
        placeMobileTrustMountInAnchor(disc);
        return;
      }

      if (!text.includes('SonicSeats') && !looksLikeLegacyLongDisclosure(text)) return;
      const wrap = disc.parentElement;
      if (!wrap || wrap.children.length > 8) return;
      removePortaledGuaranteeLayers();
      wrap.innerHTML = buildDesktopEventInfoRightColHtml();
    });

    // 2) Mobile TN often paints the ⓘ resale blurb outside `.event-info-disclaimer`.
    if (isSeaticsNativeMobileViewport()) {
      const existingMount = root.querySelector(`#${PROMOTIX_MOBILE_TRUST_MOUNT_ID}`);
      if (
        existingMount instanceof HTMLElement &&
        mobileTrustStripIsComplete(existingMount) &&
        existingMount.getBoundingClientRect().height >= 8
      ) {
        hideEmptyMobileInfoShells(root);
        return;
      }
      findLegacyBlurbHosts(root).forEach(replaceHostWithMobileTrustStrip);
      // Prefer hiding the leftover ⓘ bar over injecting a second trust strip under Show Map.
      hideEmptyMobileInfoShells(root);
    }
  });
};

/** Keep Seatics event-name chrome in sync after SPA listing changes (stale TN paint). */
const syncSeaticsEventTitleFromListing = () => {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  const title = String(window.__promotixSeaticsEventTitle || '').trim();
  if (!title) return;
  getSeaticsChromeRoots().forEach(root => {
    root.querySelectorAll('#sea-event-info-name, .event-info-name').forEach(el => {
      if (!(el instanceof HTMLElement)) return;
      const current = String(el.textContent || '').replace(/\s+/g, ' ').trim();
      if (current !== title) {
        el.textContent = title;
      }
    });
  });
};

const patchSeaticsDisclaimerConfig = () => {
  if (!window.Seatics) return;
  applyPromotixSeaticsUiConfig(window.Seatics);
  const desktopHtml = buildDesktopEventInfoRightColHtml();
  const mobileHtml = buildMobileEventInfoRightColHtml();
  ensureSeaticsLayoutStyles();
  if (window.Seatics.config) {
    window.Seatics.config.desktopEventInfoRightCol = desktopHtml;
    window.Seatics.config.mobileEventInfoRightCol = mobileHtml;
    const pol = window.Seatics.config.policiesPage;
    if (typeof pol === 'string' && /sonicseats/i.test(pol)) {
      window.Seatics.config.policiesPage = promotixPoliciesUrl();
    }
  }
  if (window.Seatics.dbConfig) {
    window.Seatics.dbConfig.desktopEventInfoRightCol = desktopHtml;
    window.Seatics.dbConfig.mobileEventInfoRightCol = mobileHtml;
  }
  if (window.Seatics.Text) {
    scrubSonicSeatsFromSeaticsText(window.Seatics.Text);
    scrubLegacySeaticsResaleDisclaimerInText(window.Seatics.Text);
  }
  syncSeaticsEventTitleFromListing();
  patchDisclaimerDomIfNeeded();
  patchSeeOtherDatesLinkDomIfNeeded();
  patchSeaticsPreCheckoutCta();
};

/** Match Seatics date/time chrome across desktop + native mobile variants. */
const DATE_TIME_HOST_SELECTORS = [
  '.event-info-date-time-span',
  '[class*="event-info-date"]',
  '[class*="date-time"]',
  '[class*="DateTime"]',
  '[class*="event-date"]',
];

const looksLikeSeaticsDateTimeText = text => {
  const t = String(text || '').replace(/\s+/g, ' ').trim();
  if (!t || t.length > 80) return false;
  // e.g. "FRI Sep 25, 2026 6:40 PM" or "Fri, Sep 25, 2026 @ 6:40PM"
  return (
    /\b(20\d{2})\b/.test(t) &&
    /\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\b/i.test(t) &&
    /\d{1,2}:\d{2}\s*[ap]m\b/i.test(t)
  );
};

const findSeaticsDateTimeHosts = root => {
  if (!(root instanceof Element)) return [];
  const found = [];
  const seen = new Set();

  DATE_TIME_HOST_SELECTORS.forEach(sel => {
    root.querySelectorAll(sel).forEach(el => {
      if (!(el instanceof HTMLElement) || seen.has(el)) return;
      seen.add(el);
      found.push(el);
    });
  });

  // Fallback: scan small text nodes that look like an event date/time line.
  if (!found.length) {
    root.querySelectorAll('div, span, p, time').forEach(el => {
      if (!(el instanceof HTMLElement) || seen.has(el)) return;
      if (el.querySelector('a, button, table, img')) return;
      const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
      if (!looksLikeSeaticsDateTimeText(text)) return;
      // Prefer the innermost matching element.
      const childHas = Array.from(el.children).some(c =>
        looksLikeSeaticsDateTimeText((c.textContent || '').replace(/\s+/g, ' ').trim())
      );
      if (childHas) return;
      seen.add(el);
      found.push(el);
    });
  }

  return found;
};

const boldSeaticsDateTimeHosts = () => {
  getSeaticsChromeRoots().forEach(root => {
    findSeaticsDateTimeHosts(root).forEach(el => {
      if (!(el instanceof HTMLElement)) return;
      el.setAttribute(PROMOTIX_DATE_BOLD_ATTR, '1');
      el.style.setProperty('font-weight', '700', 'important');
      el.querySelectorAll('span, time').forEach(child => {
        if (child instanceof HTMLElement) {
          child.style.setProperty('font-weight', '700', 'important');
        }
      });
    });
  });
};

const removeOrphanSeeOtherDatesLinks = keep => {
  if (typeof document === 'undefined') return;
  document.querySelectorAll('a.promotix-see-other-dates').forEach(el => {
    if (keep && el === keep) return;
    el.remove();
  });
};

/** Unwrap any legacy column wraps from the previous desktop placement approach. */
const unwrapAllSeeOtherDatesWraps = () => {
  document.querySelectorAll(`[${SEE_OTHER_DATES_WRAP_ATTR}="1"], .promotix-see-other-dates-wrap`).forEach(wrap => {
    if (!(wrap instanceof HTMLElement)) return;
    const parent = wrap.parentElement;
    if (!parent) return;
    while (wrap.firstChild) {
      parent.insertBefore(wrap.firstChild, wrap);
    }
    wrap.remove();
  });
};

/** Date/time text host (prefer TN's date-time span). Same for mobile + desktop. */
const findDateTimeHost = root => {
  const hosts = findSeaticsDateTimeHosts(root);
  if (!hosts.length) return null;
  const preferred = hosts.find(
    h => h instanceof HTMLElement && h.classList.contains('event-info-date-time-span')
  );
  if (preferred) return preferred;
  return hosts.reduce((best, el) => {
    if (!(el instanceof HTMLElement)) return best;
    if (!(best instanceof HTMLElement)) return el;
    return (el.textContent || '').length < (best.textContent || '').length ? el : best;
  }, null);
};

const createSeeOtherDatesLink = resolvedHref => {
  const a = document.createElement('a');
  a.className = 'promotix-see-other-dates';
  a.setAttribute(SEE_OTHER_DATES_ATTR, '1');
  a.href = resolvedHref;
  a.textContent = 'See other dates';
  return a;
};

/**
 * Open overflow/height clamps on the date card so a sibling link under the
 * date/time host is not clipped (desktop TN chrome often clamps the card).
 */
const unclipSeeOtherDatesAncestors = fromEl => {
  let el = fromEl instanceof HTMLElement ? fromEl : null;
  for (let i = 0; i < 6 && el instanceof HTMLElement; i += 1) {
    if (
      el.id === 'event-info-area' ||
      el.id === SEATICS_MAP_CONTAINER_ID ||
      el.id === 'sea-mp' ||
      el.classList.contains('event-info-details-ctn')
    ) {
      break;
    }
    el.style.setProperty('overflow', 'visible', 'important');
    el.style.setProperty('overflow-x', 'visible', 'important');
    el.style.setProperty('overflow-y', 'visible', 'important');
    el.style.setProperty('height', 'auto', 'important');
    el.style.setProperty('max-height', 'none', 'important');
    el = el.parentElement;
  }
};

/**
 * Place the link as the next sibling of the date/time host — the placement that
 * already works on mobile. Do not append inside the host (TN rewrites/clips it
 * on desktop).
 */
const placeSeeOtherDatesLinkAfterHost = (host, resolvedHref) => {
  if (!(host instanceof HTMLElement)) return null;
  unclipSeeOtherDatesAncestors(host.parentElement || host);

  const next = host.nextElementSibling;
  if (
    next instanceof HTMLAnchorElement &&
    next.classList.contains('promotix-see-other-dates')
  ) {
    next.setAttribute('href', resolvedHref);
    next.setAttribute(SEE_OTHER_DATES_ATTR, '1');
    next.classList.remove('promotix-see-other-dates-desktop');
    return next;
  }

  // Recover a link left inside the host from older desktop appendChild attempts.
  const trapped = host.querySelector('a.promotix-see-other-dates');
  if (trapped instanceof HTMLAnchorElement) {
    trapped.setAttribute('href', resolvedHref);
    trapped.setAttribute(SEE_OTHER_DATES_ATTR, '1');
    trapped.classList.remove('promotix-see-other-dates-desktop');
    host.insertAdjacentElement('afterend', trapped);
    return trapped;
  }

  const link = createSeeOtherDatesLink(resolvedHref);
  host.insertAdjacentElement('afterend', link);
  return link;
};

/**
 * Insert a single "See other dates" link immediately after the date/time host
 * (mobile above Show Map; desktop under the SAT/date/time box).
 *
 * @param {string|null|undefined} href
 *   - string: place/update the link
 *   - null: resolved "no other dates" — remove any existing link
 *   - undefined: observer re-patch — use window href; if still unset, leave DOM alone
 */
const patchSeeOtherDatesLinkDomIfNeeded = (href = undefined) => {
  if (typeof document === 'undefined') return;
  boldSeaticsDateTimeHosts();
  ensureSeaticsLayoutStyles();
  // Undo prior wrap-based placement that stretched the header flex row.
  unwrapAllSeeOtherDatesWraps();
  document.querySelectorAll('.promotix-see-other-dates-slot').forEach(el => {
    const parent = el.parentElement;
    if (!parent) {
      el.remove();
      return;
    }
    while (el.firstChild) parent.insertBefore(el.firstChild, el);
    el.remove();
  });
  // Drop absolute-positioned leftovers from e8a4f2e / earlier desktop attempts.
  document.querySelectorAll('a.promotix-see-other-dates-desktop').forEach(el => {
    el.classList.remove('promotix-see-other-dates-desktop');
    el.style.removeProperty('position');
    el.style.removeProperty('left');
    el.style.removeProperty('top');
  });

  const resolvedHref =
    typeof href === 'string'
      ? href
      : href === null
      ? null
      : typeof window !== 'undefined'
      ? window.__promotixSeeOtherDatesHref || null
      : null;

  if (!resolvedHref) {
    // Only wipe when enrichment explicitly resolved to no href.
    // Observer re-patches (href === undefined) while async resolve is pending must not wipe.
    if (href === null) {
      removeOrphanSeeOtherDatesLinks(null);
    }
    return;
  }

  if (typeof window !== 'undefined') {
    window.__promotixSeeOtherDatesHref = resolvedHref;
  }

  const roots = getSeaticsChromeRoots();
  if (!roots.length) return;

  const preferMobile = isSeaticsNativeMobileViewport();
  const orderedRoots = preferMobile
    ? [
        ...roots.filter(r => r.id === 'sea-mp'),
        ...roots.filter(r => r.id !== 'sea-mp'),
      ]
    : [
        ...roots.filter(r => r.id === SEATICS_MAP_CONTAINER_ID || r.id === 'tn-maps-inner'),
        ...roots.filter(r => r.id !== SEATICS_MAP_CONTAINER_ID && r.id !== 'tn-maps-inner'),
      ];

  // Reuse when already correctly placed as next sibling of the date/time host.
  for (let i = 0; i < orderedRoots.length; i += 1) {
    const root = orderedRoots[i];
    if (!(root instanceof Element)) continue;
    const host = findDateTimeHost(root);
    if (!(host instanceof HTMLElement)) continue;
    const next = host.nextElementSibling;
    if (
      next instanceof HTMLAnchorElement &&
      next.classList.contains('promotix-see-other-dates')
    ) {
      next.setAttribute('href', resolvedHref);
      next.setAttribute(SEE_OTHER_DATES_ATTR, '1');
      unclipSeeOtherDatesAncestors(host.parentElement || host);
      removeOrphanSeeOtherDatesLinks(next);
      return;
    }
  }

  for (let i = 0; i < orderedRoots.length; i += 1) {
    const root = orderedRoots[i];
    if (!(root instanceof Element)) continue;
    const host = findDateTimeHost(root);
    if (!(host instanceof HTMLElement)) continue;
    const link = placeSeeOtherDatesLinkAfterHost(host, resolvedHref);
    if (link) {
      removeOrphanSeeOtherDatesLinks(link);
      return;
    }
  }

  // Placement failed this tick (TN chrome not ready) — do NOT wipe a valid href's link.
  const keep = document.querySelector('a.promotix-see-other-dates');
  if (keep) removeOrphanSeeOtherDatesLinks(keep);
};

const resolvePerformerEventsPath = enrichment => {
  const performers = Array.isArray(enrichment?.performers) ? enrichment.performers : [];
  const withEvents = performers.find(p => p?.eventsPath);
  if (withEvents?.eventsPath) return String(withEvents.eventsPath);
  // Fall back to artist hub path + /events/ when eventsPath was omitted.
  const withPath = performers.find(p => p?.path);
  if (withPath?.path) {
    return `${String(withPath.path).replace(/\/?$/, '/')}events/`;
  }
  return null;
};

/** Performer calendar first, then venue calendar (show title variants often miss artist pages). */
const resolveCalendarEventsPath = enrichment => {
  const performerPath = resolvePerformerEventsPath(enrichment);
  if (performerPath) return performerPath;
  const venuePath = enrichment?.venue?.eventsPath;
  if (venuePath) return String(venuePath);
  return null;
};

/**
 * "Artist - Show Title" listing names often lack their own artist page while the
 * parent artist does (e.g. Cirque du Soleil - Drawn To Life → Cirque du Soleil).
 */
const parentPerformerNameCandidates = performersParam => {
  const raw = String(performersParam || '').trim();
  if (!raw) return [];
  const parts = raw
    .split(/\s*(?:,|&|\/|\band\b)\s*/i)
    .map(s => s.trim())
    .filter(Boolean);
  const out = [];
  parts.forEach(name => {
    const dash = name.match(/^(.*?)\s+[-–—]\s+(.+)$/);
    if (dash && dash[1] && dash[1].length >= 3) {
      const parent = dash[1].trim();
      if (parent && !out.includes(parent)) out.push(parent);
    }
  });
  return out;
};

/**
 * Show "See other dates" when a performer or venue hub calendar path exists.
 * Retries parent artist names when the listing-only show title has no page.
 */
const resolveSeeOtherDatesHref = async listing => {
  if (!listing) return null;
  const publicData = listing?.attributes?.publicData || {};
  const params = enrichmentParamsFromPublicData(publicData);
  if (!params) return null;
  const cacheKey = JSON.stringify(params);
  let enrichment = getCachedListingEnrichment(cacheKey);
  if (!enrichment) {
    enrichment = await fetchListingEnrichment(params);
    setCachedListingEnrichment(cacheKey, enrichment);
  }
  let eventsPath = resolveCalendarEventsPath(enrichment);

  // Retry with parent artist names when show-title performers have no calendar.
  if (!eventsPath && params.performers) {
    const parents = parentPerformerNameCandidates(params.performers);
    for (let i = 0; i < parents.length; i += 1) {
      const retryParams = { ...params, performers: parents[i] };
      const retryKey = JSON.stringify(retryParams);
      let retryEnrichment = getCachedListingEnrichment(retryKey);
      if (!retryEnrichment) {
        retryEnrichment = await fetchListingEnrichment(retryParams);
        setCachedListingEnrichment(retryKey, retryEnrichment);
      }
      eventsPath = resolveCalendarEventsPath(retryEnrichment);
      if (eventsPath) break;
    }
  }

  if (!eventsPath) return null;

  // Best-effort: confirm another date exists. If the related API fails/empty,
  // still expose the calendar hub when the path is live.
  const eventLabel = resolveEventEntityName({
    title: listing?.attributes?.title,
    publicData,
  });
  const base = sameOriginApiBase();
  if (!base || typeof fetch !== 'function' || !eventLabel || eventLabel === 'Event') {
    return eventsPath;
  }
  const listingId = listingIdFromListing(listing);
  const query = new URLSearchParams({
    eventName: eventLabel,
    ...(listingId ? { excludeListingId: String(listingId) } : {}),
    ...(publicData.ticketNetworkEventId
      ? { excludeEventId: String(publicData.ticketNetworkEventId) }
      : {}),
    limit: '1',
  }).toString();

  try {
    // Warm related-listings cache for other listing modules; href does not depend on it.
    void fetch(`${base}/api/seo-discovery/related-listings?${query}`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
  } catch (e) {
    // ignore
  }
  return eventsPath;
};

/** Guarantee modal uses injected HTML only — bind show/hide via delegation (no inline handlers). */
const installGuaranteeModalHandlers = cancelled => {
  if (typeof document === 'undefined') return () => {};
  const onClick = e => {
    if (cancelled()) return;
    const el = e.target;
    if (!(el instanceof Element)) return;
    const showBtn = el.closest('#event-info-guarantee-show');
    if (showBtn) {
      portGuaranteeModalLayersToBody();
      const panel = document.getElementById('event-info-guarantee');
      const backdrop = document.getElementById('event-info-guarantee-backdrop');
      if (panel) {
        panel.style.display = 'block';
        showBtn.setAttribute('aria-expanded', 'true');
        if (typeof panel.focus === 'function') {
          try {
            panel.focus({ preventScroll: true });
          } catch {
            panel.focus();
          }
        }
      }
      if (backdrop) backdrop.style.display = 'block';
      e.preventDefault();
    }
    if (el.closest('#event-info-guarantee-close') || el.id === 'event-info-guarantee-backdrop') {
      const panel = document.getElementById('event-info-guarantee');
      const backdrop = document.getElementById('event-info-guarantee-backdrop');
      const openBtn = document.getElementById('event-info-guarantee-show');
      if (panel) panel.style.display = 'none';
      if (backdrop) backdrop.style.display = 'none';
      if (openBtn) openBtn.setAttribute('aria-expanded', 'false');
      e.preventDefault();
    }
  };
  document.addEventListener('click', onClick, false);
  return () => {
    document.removeEventListener('click', onClick, false);
    removePortaledGuaranteeLayers();
  };
};

const loadJQueryForSeatics = () => {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.jQuery && window.jQuery.fn) return Promise.resolve();
  return new Promise(resolve => {
    const done = () => resolve();
    const existing = document.querySelector(`script[${SEATICS_JQUERY_SCRIPT_ATTR}]`);
    if (existing) {
      if (window.jQuery && window.jQuery.fn) done();
      else {
        existing.addEventListener('load', done);
        existing.addEventListener('error', done);
      }
      return;
    }
    const s = document.createElement('script');
    s.type = 'text/javascript';
    s.async = false;
    s.src = seaticsJqueryUrl;
    s.setAttribute(SEATICS_JQUERY_SCRIPT_ATTR, '1');
    s.onload = done;
    s.onerror = () => {
      console.error('[Seatics] jQuery failed to load from', seaticsJqueryUrl);
      done();
    };
    (document.head || document.body).appendChild(s);
  });
};

const formatAvailabilityFetchError = (err, requestUrl) => {
  const m = err && err.message ? String(err.message) : '';
  const cause = err && err.cause && err.cause.message ? ` — ${err.cause.message}` : '';
  if (/Availability HTTP \d{3}/i.test(m)) {
    return `${m}${cause}`;
  }
  const isNetwork =
    err instanceof TypeError ||
    /^(failed to fetch|fetch failed|networkerror|load failed|network request failed)\b/i.test(
      m.trim()
    ) ||
    (/failed to fetch|networkerror|load failed/i.test(m) && !/\bHTTP \d{3}\b/.test(m));
  if (isNetwork) {
    return `Network error for ${requestUrl} (${m || 'TypeError'}${cause}). The browser never got an HTTP response — check DevTools → Network (red row), server/proxy for /api, and TLS.`;
  }
  return m ? `${m}${cause}` : `Unknown error for ${requestUrl}${cause}`;
};

/**
 * Fetch ticket groups from our availability API and feed them to `Seatics.addTicketData()`.
 * Per the Seatics Maps API Integration Guide (v3.18), the pre-built UI MapAndLayout script
 * creates the map + chrome, but the ticket list is populated only when `Seatics.addTicketData()`
 * is called with a ticket-group array. Without this call the map renders with no sections.
 *
 * @param {Function} [onOutcome] called with 'ready'|'empty'|'trafficError'|'error' (once settled)
 */
const fetchAndInjectTicketData = async (
  eventId,
  listingInstanceKey,
  listingId,
  cancelled,
  onOutcome,
  listingForAnalytics = null
) => {
  if (!eventId || typeof window === 'undefined') return;
  const loadStartedAt =
    typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
  const loadDurationMs = () => {
    const end =
      typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
    return Math.round(end - loadStartedAt);
  };
  const report = outcome => {
    if (cancelled()) return;
    if (typeof onOutcome === 'function') onOutcome(outcome);
  };
  try {
    const result = await fetchTnEventAvailability(eventId, 1, listingId);
    const { ok, status, data } = result;
    const outcome = classifyTnAvailabilityOutcome(result, isTnUpstreamTrafficError);
    if (!ok) {
      console.warn('[Seatics] availability fetch for addTicketData failed', status);
      report(outcome);
      return;
    }
    const tgsRaw = Array.isArray(data?.ticketGroups) ? data.ticketGroups : [];
    const tgs = tgsRaw.map(normalizeTicketGroupPricing);
    if (!tgs.length) {
      console.warn('[Seatics] availability returned 0 ticket groups — map will show no inventory');
      if (listingForAnalytics) {
        trackSecondaryInventoryEmpty({
          listing: listingForAnalytics,
          currency: listingForAnalytics?.attributes?.price?.currency || 'USD',
          inventoryLoadDurationMs: loadDurationMs(),
          loadKey: listingInstanceKey || eventId,
        });
      }
      report(MAP_INVENTORY_EMPTY);
      return;
    }
    if (listingForAnalytics) {
      const { lowestUnitPrice, highestUnitPrice } = summarizeTicketGroupPrices(tgs);
      trackSecondaryInventoryLoaded({
        listing: listingForAnalytics,
        ticketGroupCount: tgs.length,
        inventoryCount: tgs.length,
        lowestUnitPrice,
        highestUnitPrice,
        currency: listingForAnalytics?.attributes?.price?.currency || 'USD',
        inventoryLoadDurationMs: loadDurationMs(),
        loadKey: listingInstanceKey || eventId,
      });
    }
    report(MAP_INVENTORY_READY);
    if (cancelled()) return;
    const state = (window[SEATICS_TICKET_DATA_STATE_KEY] =
      window[SEATICS_TICKET_DATA_STATE_KEY] || Object.create(null));
    const stateKey = String(listingInstanceKey || eventId);
    const signature = tgs
      .map(tg => `${String(tg?.tgID || tg?.tgId || '')}:${String(tg?.tgPrice ?? '')}`)
      .join('|');
    const prev = state[stateKey];
    if (prev?.signature === signature && prev?.completed) {
      return;
    }

    const mark = patch => {
      state[stateKey] = {
        ...(state[stateKey] || {}),
        signature,
        updatedAt: Date.now(),
        ...patch,
      };
    };
    const hasInventoryPainted = () => {
      const root = document.getElementById(SEATICS_MAP_CONTAINER_ID) || document.getElementById('sea-mp');
      if (!root) return false;
      const txt = (root.textContent || '').slice(0, 20000);
      return /\bRow\s+[A-Za-z0-9-]+\b|\bTotal Price\s*\$|\bTicket Price\s*\$|\bFees Included\b/i.test(
        txt
      );
    };

    const waitForSeatics = (retries = 0) => {
      if (cancelled()) return;
      if (window.Seatics && typeof window.Seatics.addTicketData === 'function') {
        const inject = source => {
          if (cancelled()) return;
          if (!(window.Seatics && typeof window.Seatics.addTicketData === 'function')) return;
          // Re-injecting ticket groups while the quantity drawer is open remounts
          // the list and flashes the drawer shut (common on first mobile tap).
          if (isSeaticsPrecheckoutUiOpen(document)) {
            mark({ deferredWhilePrecheckout: true, lastSource: source });
            return false;
          }
          // Apply config (incl. native per-row buyButtonContentHtml) BEFORE painting rows.
          applyPromotixSeaticsUiConfig(window.Seatics);
          window.Seatics.addTicketData(tgs);
          const calls = Number(state[stateKey]?.calls || 0) + 1;
          mark({ calls, lastSource: source, ticketGroups: tgs });
          return true;
        };

        // Primary injection once.
        inject('initial');
        // Fallback retries only if inventory is still not painted.
        [1200, 3200].forEach((delay, idx) => {
          setTimeout(() => {
            if (cancelled() || hasInventoryPainted()) {
              mark({ completed: hasInventoryPainted() });
              return;
            }
            if (isSeaticsPrecheckoutUiOpen(document)) {
              mark({ deferredWhilePrecheckout: true });
              return;
            }
            // Prevent unbounded append behavior.
            if (Number(state[stateKey]?.calls || 0) > idx + 1) return;
            inject(`retry-${idx + 1}`);
            mark({ completed: hasInventoryPainted() });
          }, delay);
        });
        console.log(`[Seatics] addTicketData called with ${tgs.length} ticket groups`);
      } else if (retries < 40) {
        setTimeout(() => waitForSeatics(retries + 1), 250);
      } else {
        console.warn('[Seatics] Seatics.addTicketData never became available after MapAndLayout load');
      }
    };
    waitForSeatics();
  } catch (e) {
    console.warn('[Seatics] ticket data fetch for map failed', e && e.message);
    report(MAP_INVENTORY_ERROR);
  }
};

/**
 * Wire `Seatics.Presentation.redirectToCheckout` so the pre-built UI Buy buttons
 * trigger our lock → initiate → order-details flow instead of the default TN checkout.
 * Uses refs (getCurrentUser / getHistory / getRoutes) to read latest values at click time.
 */
const installCheckoutRedirect = (
  listing,
  getCurrentUser,
  getHistory,
  getRoutes,
  getPrepareCheckoutPage,
  getRequestRequoteDecision,
  cancelled
) => {
  let checkoutInFlight = false;
  const attempt = (retries = 0) => {
    if (cancelled()) return;
    if (
      window.Seatics &&
      window.Seatics.Presentation &&
      typeof window.Seatics.Presentation === 'object'
    ) {
      window.Seatics.Presentation.redirectToCheckout = (ticketGroup, qty) => {
        if (cancelled()) return;
        const history = getHistory();
        const routes = getRoutes();
        const currentUser = getCurrentUser();
        const tgId = String(ticketGroup?.tgID || ticketGroup?.tgId || '').trim();
        const normalizedTicketGroup = normalizeTicketGroupPricing(ticketGroup);
        const quantity = resolveCheckoutQuantityFromTicketGroup(normalizedTicketGroup, qty);
        if (!quantity) {
          alert(SEATICS_CHECKOUT_QTY_REQUIRED_MSG);
          return;
        }
        const checkoutUnitPrice = Number(normalizedTicketGroup.tgListPrice);
        const allInUnitPrice = Number(normalizedTicketGroup.tgPrice);
        const wholesaleUnitPrice = Number(normalizedTicketGroup.tgWholesalePrice || 0);
        const customerId = customerIdFromUser(currentUser);
        const listingId = listingIdFromListing(listing);
        const listingSlug = createSlug(listing?.attributes?.title || 'listing');
        const eventId = String(
          listing?.attributes?.publicData?.ticketNetworkEventId || ''
        ).trim();
        const seatSel = seatSelectionFromTicketGroup(normalizedTicketGroup);
        trackSecondaryTicketSelected({
          listing,
          ticketGroupId: tgId,
          ticketSection: seatSel.section,
          ticketRow: seatSel.row,
          ticketQuantity: quantity,
          ticketUnitPrice: allInUnitPrice,
          value: roundMoney(allInUnitPrice * quantity),
          currency: listing?.attributes?.price?.currency || 'USD',
        });
        const requestRequoteDecision =
          typeof getRequestRequoteDecision === 'function' ? getRequestRequoteDecision() : null;
        // Lock only — do not create a Sharetribe request-payment here. Logged-in Buy used
        // to initiate immediately, then TransactionPage bounced to checkout with empty
        // orderData. Rewards/TEAK then created a second tx (no ticketGroupId) and the
        // first one expired. Guest and logged-in both go to CheckoutPage with tnSelection.
        if (!listingId || !tgId || !eventId || checkoutUnitPrice <= 0 || allInUnitPrice <= 0) {
          console.warn('[Seatics] redirectToCheckout missing params', {
            customerId,
            listingId,
            tgId,
            eventId,
            checkoutUnitPrice,
            allInUnitPrice,
          });
          alert('Unable to process this ticket selection. Please try again.');
          return;
        }
        if (checkoutInFlight) return;
        checkoutInFlight = true;
        const idem = createLockSelectionIdempotencyKey({
          listingId,
          eventId,
          ticketGroupId: tgId,
          quantity,
        });
        showPageLoadIndicator({ persist: true });
        (async () => {
          let didNavigate = false;
          try {
            const locked = await lockSelectionWithRequote(
              {
                idempotencyKey: idem,
                listingId,
                eventId,
                ticketGroupId: tgId,
                quantity,
                unitPrice: allInUnitPrice,
              },
              requestRequoteDecision,
              {
                listUnitPrice: checkoutUnitPrice,
                wholesaleUnitPrice,
                ticketGroup: normalizedTicketGroup,
              }
            );
            if (locked.cancelled) {
              trackSecondaryLockFailed({
                listing,
                reason: 'requote_declined',
                ticketGroupId: tgId,
              });
              return;
            }
            const { lockData } = locked;
            trackSecondaryLockSucceeded({
              listing,
              lockRequestId: lockData.lockRequestId,
              ticketGroupId: locked.ticketGroupId || tgId,
              quantity: locked.quantity,
            });

            const checkoutPath = createResourceLocatorString(
              'CheckoutPage',
              routes,
              { id: listingId, slug: listingSlug },
              {}
            );
            const lockExpiresAt = lockData.quote?.lockExpiry || null;
            const initialValues = {
              listing: listingForTnCheckout(listing, locked.listUnitPrice),
              orderData: {
                quantity: locked.quantity,
                tnSelection: {
                  lockRequestId: lockData.lockRequestId,
                  ticketGroupId: locked.ticketGroupId,
                  eventId,
                  // unitPrice is our wholesale COST (fulfillment bids it to Mercury).
                  // listUnitPrice is what the customer's item line must be priced at.
                  ...(locked.wholesaleUnitPrice > 0
                    ? { unitPrice: locked.wholesaleUnitPrice }
                    : {}),
                  listUnitPrice: locked.listUnitPrice,
                  allInUnitPrice: locked.allInUnitPrice,
                  quantity: locked.quantity,
                  ...(Number(normalizedTicketGroup.tgQty) > 0
                    ? { ticketGroupQty: Number(normalizedTicketGroup.tgQty) }
                    : {}),
                  ...(lockExpiresAt ? { lockExpiresAt: String(lockExpiresAt) } : {}),
                  ...seatSelectionFromTicketGroup(normalizedTicketGroup),
                  ...deliveryMethodsFromLockData(lockData),
                },
              },
              confirmPaymentError: null,
            };
            const prepareCheckout =
              typeof getPrepareCheckoutPage === 'function' ? getPrepareCheckoutPage() : null;
            if (typeof prepareCheckout === 'function') {
              prepareCheckout(initialValues, true);
            } else {
              console.warn(
                '[Seatics] prepareCheckoutPage is not wired; checkout may show a stale listing. Pass prepareCheckoutPage from ListingPage.'
              );
            }
            trackOpenAIAdsCheckoutStarted({
              listing: initialValues.listing,
              orderData: initialValues.orderData,
            });
            trackSecondaryBeginCheckout({
              listing: initialValues.listing,
              orderData: initialValues.orderData,
              dedupeKey: lockData.lockRequestId || `${listingId}:${tgId}`,
              // Ads/analytics revenue is the all-in price the customer actually pays.
              fallbackUnitPriceMajor: locked.allInUnitPrice,
            });
            didNavigate = true;
            markSeaticsCheckoutNavigation();
            history.push(checkoutPath);
          } catch (e) {
            console.error('[Seatics] checkout lock failed', e);
            trackSecondaryLockFailed({
              listing,
              reason: classifySecondaryLockFailureReason(e),
              status: e?.status,
              code: e?.data?.code || e?.code,
              ticketGroupId: tgId,
            });
            hidePageLoadIndicator();
            alert(checkoutAlertMessage(e));
          } finally {
            checkoutInFlight = false;
            if (!didNavigate) {
              hidePageLoadIndicator();
            }
          }
        })();
      };
      console.log('[Seatics] redirectToCheckout installed');
    } else if (retries < 40) {
      setTimeout(() => attempt(retries + 1), 250);
    }
  };
  attempt();
};

/**
 * Apply Seatics.config and Seatics.Text overrides for the pre-built UI.
 * Must run after MapAndLayout loads (polls until `Seatics.config` exists).
 */
const applySeaticsConfig = cancelled => {
  let reapplyTimer = null;
  let observer = null;
  let observerDebounce = null;
  const runPatches = () => {
    if (cancelled()) return;
    // Opening the quantity drawer mutates Seatics DOM; patching/config reapply
    // in that window remounts chrome and closes the drawer on the first tap.
    if (isSeaticsPrecheckoutUiOpen(document)) {
      return;
    }
    patchSeaticsDisclaimerConfig();
    patchSeeOtherDatesLinkDomIfNeeded();
    markNativeWebViewIfNeeded();
    // Seatics re-initialises its config on re-render, so keep reasserting ours
    // (notably fullyShownMapHeight) rather than setting it once.
    applyPromotixSeaticsUiConfig(window.Seatics);
    syncSeaticsViewportHeight(document, window);
  };
  const attempt = (retries = 0) => {
    if (cancelled()) return;
    if (window.Seatics && window.Seatics.config) {
      applyPromotixSeaticsUiConfig(window.Seatics);
      runPatches();

      let n = 0;
      reapplyTimer = setInterval(() => {
        if (cancelled()) {
          clearInterval(reapplyTimer);
          return;
        }
        n += 1;
        // Keep fighting TN re-renders longer on mobile while the trust mount is missing.
        const trustOk =
          !isSeaticsNativeMobileViewport() ||
          (() => {
            const m = document.getElementById(PROMOTIX_MOBILE_TRUST_MOUNT_ID);
            return m instanceof HTMLElement && mobileTrustStripIsComplete(m) && m.getBoundingClientRect().height >= 8;
          })();
        // The full DOM pass costs ~85ms on a mid phone (list-wide scans + forced style
        // recalcs); every 400ms it blocked ~20% of the main thread and delayed taps (INP).
        // The observer below re-patches within 80ms of a Seatics re-render, so once the
        // trust strip is in place this is only a 2s fallback; the config object is cheap.
        if (!trustOk || n % 5 === 0) {
          runPatches();
        } else if (!isSeaticsPrecheckoutUiOpen(document)) {
          applyPromotixSeaticsUiConfig(window.Seatics);
        }
        if (n >= 90 && trustOk) {
          clearInterval(reapplyTimer);
        }
        if (n >= 180) {
          clearInterval(reapplyTimer);
        }
      }, 400);

      // TN re-renders the ⓘ blurb / date chrome after our first patch — keep watching.
      if (typeof MutationObserver !== 'undefined') {
        observer = new MutationObserver(() => {
          if (cancelled()) return;
          // Observer callbacks run before paint. Header edits deferred to the debounce
          // grew #event-info-area ~21px after Seatics painted it (mobile CLS).
          if (!isSeaticsPrecheckoutUiOpen(document)) {
            syncSeaticsEventTitleFromListing();
            if (
              window.__promotixSeeOtherDatesHref &&
              !document.querySelector('a.promotix-see-other-dates')
            ) {
              patchSeeOtherDatesLinkDomIfNeeded();
            }
          }
          if (observerDebounce) clearTimeout(observerDebounce);
          observerDebounce = setTimeout(runPatches, 80);
        });
        getSeaticsChromeRoots().forEach(root => {
          observer.observe(root, { childList: true, subtree: true, characterData: true });
        });
        // #sea-mp may appear after first paint.
        const bodyObs = new MutationObserver(() => {
          if (cancelled()) return;
          getSeaticsChromeRoots().forEach(root => {
            try {
              observer.observe(root, { childList: true, subtree: true, characterData: true });
            } catch (e) {
              // ignore
            }
          });
          runPatches();
        });
        if (document.body) {
          bodyObs.observe(document.body, { childList: true, subtree: false });
          observer._promotixBodyObs = bodyObs;
        }
      }

      console.log('[Seatics] config overrides applied');
    } else if (retries < 40) {
      setTimeout(() => attempt(retries + 1), 250);
    }
  };
  attempt();
  return () => {
    if (reapplyTimer) clearInterval(reapplyTimer);
    if (observerDebounce) clearTimeout(observerDebounce);
    if (observer) {
      observer.disconnect();
      if (observer._promotixBodyObs) observer._promotixBodyObs.disconnect();
    }
  };
};

const SeaticsRequoteModal = ({
  id,
  modalState,
  onAccept,
  onDecline,
  onManageDisableScrolling,
}) => {
  const payload = modalState?.payload;
  return (
    <Modal
      id={id}
      isOpen={!!modalState?.open}
      onClose={onDecline}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal
    >
      <div className={css.seaticsRequoteModal}>
        <h3 className={css.seaticsRequoteTitle}>Oh no! Someone just snagged these tickets!</h3>
        <p className={css.seaticsRequoteMessage}>{buildRequoteMessage(payload)}</p>
        <div className={css.seaticsRequoteActions}>
          <SecondaryButton type="button" onClick={onDecline}>
            No
          </SecondaryButton>
          <PrimaryButton type="button" className={css.seaticsRequoteConfirm} onClick={onAccept}>
            Yes
          </PrimaryButton>
        </div>
      </div>
    </Modal>
  );
};

/** Full-width seat map for the main listing column (above gallery / hero). No iframe — one document scroll. */
export const SeaticsMapEmbed = ({
  listing,
  variant,
  layout,
  currentUser,
  history,
  routes,
  prepareCheckoutPage,
  onManageDisableScrolling,
}) => {
  const publicData = listing?.attributes?.publicData || {};
  // Skip MapAndLayout for search/LLM crawlers — SSR HTML + schema still index; saves TN Maps quota.
  const crawlerSkipSeatics = isLikelyTnSeaticsBot();
  const mapConfigured = Boolean(seaticsMapBootstrapUrl(publicData));
  // Closed listings and past-date events never boot Seatics: the outcome is known at
  // render time, so SSR paints a sold-out / date-passed panel instead (saves TN Maps +
  // Mercury quota on dead pages, and the message is in the first HTML — no spinner).
  const unavailableReason = tnListingUnavailableReason(listing);
  const shouldLoadWidget = mapConfigured && !crawlerSkipSeatics && !unavailableReason;
  const bootstrapUrl = shouldLoadWidget ? seaticsMapBootstrapUrl(publicData) : null;
  const staticMapUrl = seaticsStaticMapUrlFromListing(listing);
  const eventId = String(publicData?.ticketNetworkEventId || '').trim();
  const listingInstanceKey = `${listingIdFromListing(listing) || 'no-listing'}:${eventId || 'no-event'}`;

  const currentUserRef = useRef(currentUser);
  const historyRef = useRef(history);
  const routesRef = useRef(routes);
  useEffect(() => { currentUserRef.current = currentUser; }, [currentUser]);
  useEffect(() => { historyRef.current = history; }, [history]);
  useEffect(() => { routesRef.current = routes; }, [routes]);
  const prepareCheckoutRef = useRef(prepareCheckoutPage);
  useEffect(() => {
    prepareCheckoutRef.current = prepareCheckoutPage;
  }, [prepareCheckoutPage]);
  const requoteResolverRef = useRef(null);
  const [requoteModal, setRequoteModal] = useState(emptyRequoteModalState);
  const [mapInventoryStatus, setMapInventoryStatus] = useState(MAP_INVENTORY_LOADING);
  // Current status readable inside long-lived effect callbacks (inventory watchdog).
  const mapInventoryStatusRef = useRef(mapInventoryStatus);
  useEffect(() => {
    mapInventoryStatusRef.current = mapInventoryStatus;
  }, [mapInventoryStatus]);

  useEffect(() => {
    setMapInventoryStatus(MAP_INVENTORY_LOADING);
  }, [listingInstanceKey]);

  // Resolve performer /events/ href for "See other dates" (only when other dates exist).
  useEffect(() => {
    if (!listing || typeof window === 'undefined') return undefined;
    let cancelled = false;
    // Do not clear __promotixSeeOtherDatesHref here — Seatics observer re-patches
    // would wipe a visible link while enrichment is still in flight.
    resolveSeeOtherDatesHref(listing).then(href => {
      if (cancelled) return;
      window.__promotixSeeOtherDatesHref = href || null;
      // null = explicit "no dates" (remove); string = place/update
      patchSeeOtherDatesLinkDomIfNeeded(href || null);
    });
    return () => {
      cancelled = true;
      window.__promotixSeeOtherDatesHref = null;
      removeOrphanSeeOtherDatesLinks(null);
    };
  }, [listingInstanceKey, listing]);

  const inventorySettled =
    mapInventoryStatus === MAP_INVENTORY_EMPTY ||
    mapInventoryStatus === MAP_INVENTORY_TRAFFIC ||
    mapInventoryStatus === MAP_INVENTORY_ERROR;
  // Keep our placeholder until Mercury classifies inventory. Seatics often paints
  // "Map Coming Soon!" + skeleton rows first — treating that as "chrome ready" used
  // to dismiss the spinner and leave the page looking stuck on Seatics' empty chrome
  // (Luis Alfonso / zero-inventory). Only hide the placeholder once we know ready/empty.
  const showLoadingPlaceholder = mapInventoryStatus === MAP_INVENTORY_LOADING;
  const inventoryStatusMessage =
    mapInventoryStatus === MAP_INVENTORY_EMPTY
      ? TN_EMPTY_INVENTORY_CUSTOMER_MESSAGE
      : mapInventoryStatus === MAP_INVENTORY_TRAFFIC
      ? TN_TRAFFIC_CUSTOMER_MESSAGE
      : mapInventoryStatus === MAP_INVENTORY_ERROR
      ? 'Could not load ticket availability. Please try again.'
      : null;

  useEffect(() => {
    // Tear down Seatics empty chrome when inventory settles unavailable so the
    // sold-out / traffic overlay is the only thing in the band (no skeletons under it).
    if (!inventorySettled || typeof document === 'undefined') {
      return;
    }
    document.querySelectorAll('#sea-mp').forEach(n => {
      try {
        n.remove();
      } catch (_e) {
        /* ignore */
      }
    });
    const mapsRoot = document.getElementById(SEATICS_MAP_CONTAINER_ID);
    if (mapsRoot) {
      mapsRoot.innerHTML = '';
    }
  }, [inventorySettled, listingInstanceKey]);

  const requestRequoteDecision = (payload, pendingCheckoutArgs) =>
    new Promise(resolve => {
      requoteResolverRef.current = resolve;
      setRequoteModal({ open: true, payload, pendingCheckoutArgs });
    });
  const resolveRequoteDecision = accepted => {
    const resolver = requoteResolverRef.current;
    requoteResolverRef.current = null;
    setRequoteModal(emptyRequoteModalState);
    if (resolver) resolver(accepted);
  };
  useEffect(
    () => () => {
      if (requoteResolverRef.current) {
        requoteResolverRef.current(false);
        requoteResolverRef.current = null;
      }
    },
    []
  );

  useEffect(() => {
    if (!bootstrapUrl || typeof document === 'undefined') return undefined;

    let cancelled = false;
    const isCancelled = () => cancelled;
    let stopDisclaimerReapply = () => {};
    let releaseGuaranteeHandlers = () => {};
    let releaseSelectItemTracking = () => {};
    let releaseHashNavigationGuard = () => {};
    let releasePrecheckoutWebViewFix = () => {};
    let releaseViewportSizing = () => {};
    let releaseTicketPricePatch = () => {};
    let releaseFilterDedupePatch = () => {};
    let releaseLowestPricePillPatch = () => {};
    let releaseMobileEmbedGuard = () => {};
    let releaseStickyClearanceSync = () => {};
    let releaseDesktopForce = () => {};
    let releaseStructuredDataGuard = () => {};

    const cleanupSeaticsInjections = () => {
      document.querySelectorAll(`script[${SEATICS_BOOTSTRAP_SCRIPT_ATTR}]`).forEach(n => n.remove());
      // SPA navigations can leave more than one #sea-mp; remove every copy.
      document.querySelectorAll('#sea-mp').forEach(n => n.remove());
      document
        .querySelectorAll('#event-info-guarantee, #event-info-guarantee-backdrop')
        .forEach(n => n.remove());
      const mapsRoot = document.getElementById(SEATICS_MAP_CONTAINER_ID);
      if (mapsRoot) mapsRoot.innerHTML = '';
      // Reset Seatics globals so next listing does not reuse stale state/inventory.
      if (typeof window !== 'undefined' && window.Seatics) {
        try {
          delete window.Seatics;
        } catch (e) {
          window.Seatics = undefined;
        }
      }
      if (typeof window !== 'undefined' && window[SEATICS_TICKET_DATA_STATE_KEY]) {
        delete window[SEATICS_TICKET_DATA_STATE_KEY];
      }
      if (typeof window !== 'undefined') {
        window.__promotixSeaticsEventTitle = null;
      }
    };

    const isMobileViewport = typeof window !== 'undefined' && window.innerWidth < 992;

    if (document.body) {
      document.body.setAttribute(SEATICS_EMBED_BODY_ATTR, '1');
    }
    cleanupSeaticsInjections();
    if (typeof window !== 'undefined') {
      window.__promotixSeaticsEventTitle = listing?.attributes?.title || null;
    }
    releaseStructuredDataGuard = installStructuredDataGuard(isCancelled);
    // Strategy B on mobile: native Seatics mobile mode (no desktop force, no embed
    // guard that relocates #sea-mp into the tall #tn-maps desktop band).
    if (!isMobileViewport) {
      releaseDesktopForce = installDesktopModeForMobile();
    }
    ensureSeaticsLayoutStyles();
    markNativeWebViewIfNeeded();

    let retryTimer = null;

    /**
     * Settle the inventory UI without the Seatics widget: fetch availability directly
     * and classify. Used when MapAndLayout fails its final retry or takes too long —
     * the page must never sit on "Getting available ticket options" forever. Only
     * downgrades from LOADING; never overrides an outcome the normal path reported.
     */
    const settleInventoryStatusWithoutWidget = async () => {
      if (cancelled || !eventId) return;
      try {
        const result = await fetchTnEventAvailability(eventId, 1, listingIdFromListing(listing));
        if (cancelled) return;
        const outcome = classifyTnAvailabilityOutcome(result, isTnUpstreamTrafficError);
        setMapInventoryStatus(prev => (prev === MAP_INVENTORY_LOADING ? outcome : prev));
      } catch (e) {
        if (cancelled) return;
        setMapInventoryStatus(prev =>
          prev === MAP_INVENTORY_LOADING ? MAP_INVENTORY_ERROR : prev
        );
      }
    };

    // Watchdog: if the widget has neither painted chrome nor settled inventory after
    // this long, classify availability directly so the spinner resolves to a real state.
    const INVENTORY_SETTLE_WATCHDOG_MS = 12000;
    const inventoryWatchdog = setTimeout(() => {
      if (cancelled) return;
      if (mapInventoryStatusRef.current !== MAP_INVENTORY_LOADING) return;
      if (seaticsMapSurfaceReady()) return;
      settleInventoryStatusWithoutWidget();
    }, INVENTORY_SETTLE_WATCHDOG_MS);

    /**
     * Transient script-tag failures (network blip, upstream 401 that slipped through
     * the proxy's auto-retry) get one more attempt with a cache-buster query param.
     * Combined with the server-side OAuth refresh + stale-cache fallback this turns
     * intermittent TN/WSO2 hiccups into a recovered render instead of a blank map.
     */
    const MAX_BOOTSTRAP_ATTEMPTS = 2;
    let bootstrapAttempts = 0;

    const buildAttemptUrl = attemptIndex => {
      if (attemptIndex <= 1) return bootstrapUrl;
      const sep = bootstrapUrl.includes('?') ? '&' : '?';
      return `${bootstrapUrl}${sep}_t=${Date.now()}`;
    };

    const appendBootstrapScript = () => {
      bootstrapAttempts += 1;
      const attempt = bootstrapAttempts;
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src = buildAttemptUrl(attempt);
      script.setAttribute(SEATICS_BOOTSTRAP_SCRIPT_ATTR, '1');
      script.setAttribute(SEATICS_INSTANCE_ATTR, listingInstanceKey);
      script.onload = () => {
        if (cancelled) return;
        // Ignore late onload from a previous route/listing.
        if (script.getAttribute(SEATICS_INSTANCE_ATTR) !== listingInstanceKey) return;
        stopDisclaimerReapply = applySeaticsConfig(isCancelled);
        releaseGuaranteeHandlers = installGuaranteeModalHandlers(isCancelled);
        releaseSelectItemTracking = installSeaticsSelectItemTracking({
          listing,
          isCancelled,
          mapContainerId: SEATICS_MAP_CONTAINER_ID,
          getTicketGroups: () => {
            const state = window[SEATICS_TICKET_DATA_STATE_KEY] || {};
            const entry = state[String(listingInstanceKey)] || state[String(eventId)] || {};
            return Array.isArray(entry.ticketGroups) ? entry.ticketGroups : [];
          },
        });
        releaseHashNavigationGuard = installSeaticsHashNavigationGuard(isCancelled);
        releasePrecheckoutWebViewFix = installSeaticsPrecheckoutWebViewFix(isCancelled);
        releaseViewportSizing = installSeaticsViewportSizing(isCancelled);
        // CLS-safe: re-apply reserved (CSS-var) top clearance whenever Seatics
        // mutates #sea-mp. Never grows past first-paint reservation.
        releaseStickyClearanceSync = installSeaticsStickyClearanceSync(isCancelled);
        releaseTicketPricePatch = installTicketPriceBreakdownPatch(isCancelled);
        releaseFilterDedupePatch = installFilterDedupePatch(isCancelled);
        releaseLowestPricePillPatch = installLowestPricePillPatch(isCancelled);
        // Desktop-only: contain forced-desktop chrome. Never run on native mobile —
        // moving #sea-mp into #tn-maps expands collapsed "Show Map" into a huge band.
        if (!isMobileViewport) {
          releaseMobileEmbedGuard = installSeaticsMobileEmbedGuard(isCancelled);
        }
        syncSeaticsEventTitleFromListing();
        removeThirdPartyEventStructuredData();
        if (eventId) {
          fetchAndInjectTicketData(
            eventId,
            listingInstanceKey,
            listingIdFromListing(listing),
            isCancelled,
            outcome => {
              if (!isCancelled()) setMapInventoryStatus(outcome);
            },
            listing
          );
        } else {
          setMapInventoryStatus(MAP_INVENTORY_EMPTY);
        }
        installCheckoutRedirect(
          listing,
          () => currentUserRef.current,
          () => historyRef.current,
          () => routesRef.current,
          () => prepareCheckoutRef.current,
          () => requestRequoteDecision,
          isCancelled
        );
      };
      script.onerror = () => {
        if (cancelled) return;
        console.error(
          `[Seatics] MapAndLayout script failed (attempt ${attempt}/${MAX_BOOTSTRAP_ATTEMPTS}):`,
          script.src
        );
        if (attempt >= MAX_BOOTSTRAP_ATTEMPTS) {
          // Widget will never boot — resolve the loading UI to a real inventory state
          // (sold out / traffic / error) instead of an eternal spinner.
          settleInventoryStatusWithoutWidget();
          return;
        }
        // Remove the failed tag and retry once with a cache-buster. Don't reset
        // any of the install* hooks — they only fire on successful onload.
        try { script.remove(); } catch (_e) { /* ignore */ }
        retryTimer = setTimeout(() => {
          retryTimer = null;
          if (cancelled) return;
          appendBootstrapScript();
        }, 750);
      };
      document.body.appendChild(script);
    };

    (async () => {
      try {
        await loadJQueryForSeatics();
        if (cancelled) return;
        if (!(typeof window !== 'undefined' && window.jQuery && window.jQuery.fn)) {
          console.warn('[Seatics] jQuery missing before MapAndLayout; allow script-src for', seaticsJqueryUrl);
        }
        appendBootstrapScript();
      } catch (e) {
        console.error('[Seatics] map bootstrap failed', e);
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(inventoryWatchdog);
      if (retryTimer) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
      stopDisclaimerReapply();
      releaseGuaranteeHandlers();
      releaseSelectItemTracking();
      releaseHashNavigationGuard();
      releasePrecheckoutWebViewFix();
      releaseViewportSizing();
      releaseStickyClearanceSync();
      releaseTicketPricePatch();
      releaseFilterDedupePatch();
      releaseLowestPricePillPatch();
      releaseMobileEmbedGuard();
      releaseDesktopForce();
      releaseStructuredDataGuard();
      if (document.body) {
        document.body.removeAttribute(SEATICS_EMBED_BODY_ATTR);
      }
      // MapAndLayout closes the quantity sheet with history.back() when #sea-mp
      // is removed. That pop lands after we have already pushed /checkout.
      withoutHistoryBack(() => {
        cleanupSeaticsInjections();
      });
    };
  }, [bootstrapUrl, eventId, listingInstanceKey]);

  const searchMoreEventsCta = (
    <NamedLink name="SearchPage" className={css.seaticsInventoryStatusCta}>
      {TN_SEARCH_MORE_EVENTS_CTA_LABEL}
    </NamedLink>
  );

  return (
    <div
      id={PROMOTIX_SELECT_TICKETS_ANCHOR_ID}
      className={classNames(css.seaticsMapSlot, variant === 'hero' && css.seaticsMapSlotHero)}
    >
      {unavailableReason && mapConfigured && !crawlerSkipSeatics ? (
        // Closed listing or past event date — known at render time, so this panel is in
        // the SSR HTML (no Seatics boot, no spinner). Static venue map dims behind it.
        <div
          className={classNames(
            css.seaticsViewport,
            variant === 'hero' && css.seaticsViewportHero,
            layout === 'band' && css.seaticsViewportBand,
            css.seaticsViewportUnavailable
          )}
          data-seatics-chrome="unavailable"
        >
          {staticMapUrl ? (
            <img
              className={css.seaticsStaticMapLcp}
              src={staticMapUrl}
              alt=""
              decoding="async"
              loading="eager"
              fetchpriority="high"
            />
          ) : null}
          <div
            className={classNames(
              css.seaticsInventoryStatus,
              staticMapUrl && css.seaticsInventoryStatusOverMap
            )}
            role="status"
          >
            <p className={css.seaticsInventoryStatusText}>
              {unavailableReason === 'past'
                ? TN_EVENT_DATE_PASSED_CUSTOMER_MESSAGE
                : TN_EMPTY_INVENTORY_CUSTOMER_MESSAGE}
            </p>
            {searchMoreEventsCta}
          </div>
        </div>
      ) : bootstrapUrl ? (
        <div
          className={classNames(
            css.seaticsViewport,
            variant === 'hero' && css.seaticsViewportHero,
            layout === 'band' && css.seaticsViewportBand
          )}
          data-seatics-chrome="1"
        >
          {/* Keep the static map under the sold-out panel once inventory settles empty —
              same visual as the SSR closed/past branch. */}
          {staticMapUrl && (showLoadingPlaceholder || mapInventoryStatus === MAP_INVENTORY_EMPTY) ? (
            <img
              className={css.seaticsStaticMapLcp}
              src={staticMapUrl}
              alt=""
              decoding="async"
              loading="eager"
              fetchpriority="high"
            />
          ) : null}
          {showLoadingPlaceholder ? (
            <SeaticsMapLoadingPlaceholder overStaticMap={Boolean(staticMapUrl)} />
          ) : null}
          {inventoryStatusMessage ? (
            <div
              className={classNames(
                css.seaticsInventoryStatus,
                mapInventoryStatus === MAP_INVENTORY_TRAFFIC && css.seaticsInventoryStatusTraffic,
                staticMapUrl &&
                  mapInventoryStatus === MAP_INVENTORY_EMPTY &&
                  css.seaticsInventoryStatusOverMap
              )}
              role="status"
            >
              <p className={css.seaticsInventoryStatusText}>{inventoryStatusMessage}</p>
              {mapInventoryStatus === MAP_INVENTORY_EMPTY ? searchMoreEventsCta : null}
            </div>
          ) : null}
          <div
            id={SEATICS_MAP_CONTAINER_ID}
            className={classNames(
              css.seaticsMapHost,
              'seatics',
              showLoadingPlaceholder && css.seaticsMapHostPending,
              inventoryStatusMessage && css.seaticsMapHostUnavailable
            )}
            role="region"
            aria-label="Seat map and ticket list"
            aria-hidden={inventoryStatusMessage ? 'true' : undefined}
          />
        </div>
      ) : crawlerSkipSeatics ? (
        // Crawlers: static venue map only (CDN PNG). No MapAndLayout / Mercury.
        <div
          className={classNames(
            css.seaticsViewport,
            variant === 'hero' && css.seaticsViewportHero,
            layout === 'band' && css.seaticsViewportBand
          )}
          data-seatics-chrome="crawler-static"
        >
          {staticMapUrl ? (
            <img
              className={css.seaticsStaticMapLcp}
              src={staticMapUrl}
              alt=""
              decoding="async"
              loading="eager"
              fetchpriority="high"
            />
          ) : null}
        </div>
      ) : (
        <p className={css.seaticsHint}>Seatics map is not configured in frontend env.</p>
      )}
      <SeaticsRequoteModal
        id="SeaticsMapEmbed.requote"
        modalState={requoteModal}
        onAccept={() => resolveRequoteDecision(true)}
        onDecline={() => resolveRequoteDecision(false)}
        onManageDisableScrolling={onManageDisableScrolling}
      />
    </div>
  );
};

SeaticsMapEmbed.propTypes = {
  listing: propTypes.listing.isRequired,
  variant: oneOf(['hero', 'product']),
  layout: oneOf(['band', 'inline']),
  currentUser: propTypes.currentUser,
  history: shape({ push: func.isRequired }),
  routes: object,
  /** Same as ListingPage → checkout: must dispatch CheckoutPage initial state (and match URL listing). */
  prepareCheckoutPage: func,
  onManageDisableScrolling: func,
};

SeaticsMapEmbed.defaultProps = {
  variant: 'product',
  layout: 'inline',
  currentUser: null,
  history: null,
  routes: null,
  prepareCheckoutPage: null,
  onManageDisableScrolling: () => {},
};

const SeaticsEventPurchasePanel = props => {
  const {
    listing,
    currentUser,
    history,
    routes,
    isOwnListing,
    onManageDisableScrolling,
    prepareCheckoutPage,
  } = props;
  const publicData = listing?.attributes?.publicData || {};
  const eventId = String(publicData.ticketNetworkEventId || '').trim();
  const [quantity, setQuantity] = useState(1);
  const [debouncedQuantity, setDebouncedQuantity] = useState(1);
  const [availability, setAvailability] = useState(null);
  const [selectedSection, setSelectedSection] = useState('');
  const [loadError, setLoadError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const checkoutAttemptInFlightRef = useRef(false);
  const [submitError, setSubmitError] = useState('');
  const [requoteModal, setRequoteModal] = useState(emptyRequoteModalState);
  const requoteResolverRef = useRef(null);
  const requestRequoteDecision = (payload, pendingCheckoutArgs) =>
    new Promise(resolve => {
      requoteResolverRef.current = resolve;
      setRequoteModal({ open: true, payload, pendingCheckoutArgs });
    });
  const resolveRequoteDecision = accepted => {
    const resolver = requoteResolverRef.current;
    requoteResolverRef.current = null;
    setRequoteModal(emptyRequoteModalState);
    if (resolver) resolver(accepted);
  };

  useEffect(
    () => () => {
      if (requoteResolverRef.current) {
        requoteResolverRef.current(false);
        requoteResolverRef.current = null;
      }
    },
    []
  );

  // GA4 view_item: product/detail viewed — do not wait for inventory.
  useEffect(() => {
    if (!isTnEventListing(listing)) return;
    trackSecondaryViewItem({ listing });
  }, [listing?.id?.uuid || listing?.id]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuantity(quantity), AVAILABILITY_QUANTITY_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [quantity]);

  useEffect(() => {
    if (!eventId) return;
    // Crawlers must not hit Mercury availability (map embed already skips for the same UA set).
    if (isLikelyTnSeaticsBot()) {
      setAvailability({ ticketGroups: [], sections: [], code: 'bot_skipped' });
      setLoadError('');
      return undefined;
    }
    let active = true;
    setLoadError('');
    setAvailability(null);
    const run = async () => {
      try {
        const { ok, status, data } = await fetchTnEventAvailability(
          eventId,
          debouncedQuantity,
          listingIdFromListing(listing)
        );
        if (!ok) {
          const detail =
            typeof data?.error === 'string'
              ? data.error
              : data && Object.keys(data).length
              ? JSON.stringify(data)
              : '(no error body)';
          throw new Error(
            isTnUpstreamTrafficError(status, data)
              ? TN_TRAFFIC_CUSTOMER_MESSAGE
              : `Availability HTTP ${status}: ${detail}`
          );
        }
        if (!active) return;
        const normalizedGroups = Array.isArray(data?.ticketGroups)
          ? data.ticketGroups.map(normalizeTicketGroupPricing)
          : [];
        setAvailability({ ...data, ticketGroups: normalizedGroups });
        const firstSection = Array.isArray(data?.sections) && data.sections.length ? data.sections[0].section : '';
        setSelectedSection(prev => (prev ? prev : firstSection));
      } catch (e) {
        if (!active) return;
        console.warn('[Seatics] availability request failed', {
          eventId,
          quantity: debouncedQuantity,
          message: e && e.message,
          cause: e && e.cause,
        });
        const msg =
          typeof e === 'string'
            ? e
            : e?.message === TN_TRAFFIC_CUSTOMER_MESSAGE
            ? TN_TRAFFIC_CUSTOMER_MESSAGE
            : formatAvailabilityFetchError(e, `/api/tn-seatics/events/${eventId}/availability`) ||
              'Could not load ticket availability. Confirm Node serves /api/tn-seatics and TN_SEATICS_BACKEND_BASE_URL is set on the server.';
        setLoadError(msg);
      }
    };
    run();
    return () => {
      active = false;
    };
  }, [eventId, debouncedQuantity]);

  const sections = Array.isArray(availability?.sections) ? availability.sections : [];
  const ticketGroups = Array.isArray(availability?.ticketGroups) ? availability.ticketGroups : [];

  const selectedTicketGroup = useMemo(() => {
    if (!selectedSection) return null;
    return (
      ticketGroups.find(g => {
        const sec = String(g?.tgUserSec || '').trim();
        const tgQty = Number(g?.tgQty || 0);
        return (
          sec === String(selectedSection) &&
          tgQty >= quantity &&
          splitBitmapAllowsQty(g?.tgSplitsBitmap, quantity)
        );
      }) || null
    );
  }, [ticketGroups, selectedSection, quantity]);

  const canSubmit =
    !isOwnListing &&
    !!eventId &&
    !submitting &&
    !!selectedTicketGroup &&
    Number(selectedTicketGroup?.tgBasePrice) > 0;

  const onStartCheckout = async () => {
    setSubmitError('');
    if (!canSubmit || checkoutAttemptInFlightRef.current) return;
    const listingId = listingIdFromListing(listing);
    if (!listingId) {
      setSubmitError('Listing is missing an ID.');
      return;
    }
    const ticketGroupId = String(selectedTicketGroup.tgID || selectedTicketGroup.tgId || '').trim();
    if (!ticketGroupId) {
      setSubmitError('Selected ticket group is invalid.');
      return;
    }
    const normalizedTicketGroup = normalizeTicketGroupPricing(selectedTicketGroup);
    if (!ticketGroupAllowsQuantity(normalizedTicketGroup, quantity)) {
      setSubmitError(
        'Please choose a valid ticket quantity for this section before continuing to checkout.'
      );
      return;
    }
    const checkoutUnitPrice = Number(normalizedTicketGroup.tgListPrice);
    const allInUnitPrice = Number(normalizedTicketGroup.tgPrice);
    const wholesaleUnitPrice = Number(normalizedTicketGroup.tgWholesalePrice || 0);
    const idem = createLockSelectionIdempotencyKey({
      listingId,
      eventId,
      ticketGroupId,
      quantity,
    });

    const seatSel = seatSelectionFromTicketGroup(normalizedTicketGroup);
    trackSecondaryTicketSelected({
      listing,
      ticketGroupId,
      ticketSection: seatSel.section,
      ticketRow: seatSel.row,
      ticketQuantity: quantity,
      ticketUnitPrice: allInUnitPrice,
      value: roundMoney(allInUnitPrice * quantity),
      currency: listing?.attributes?.price?.currency || 'USD',
    });

    checkoutAttemptInFlightRef.current = true;
    setSubmitting(true);
    showPageLoadIndicator({ persist: true });
    let didNavigate = false;
    try {
      const lockPayload = {
        idempotencyKey: idem,
        listingId,
        eventId,
        ticketGroupId,
        quantity,
        unitPrice: allInUnitPrice,
      };
      const locked = await lockSelectionWithRequote(lockPayload, requestRequoteDecision, {
        listUnitPrice: checkoutUnitPrice,
        wholesaleUnitPrice,
        ticketGroup: normalizedTicketGroup,
      });
      if (locked.cancelled) {
        trackSecondaryLockFailed({
          listing,
          reason: 'requote_declined',
          ticketGroupId,
        });
        return;
      }
      const { lockData } = locked;
      trackSecondaryLockSucceeded({
        listing,
        lockRequestId: lockData.lockRequestId,
        ticketGroupId: locked.ticketGroupId || ticketGroupId,
        quantity: locked.quantity,
      });
      const lockExpiresAt = lockData.quote?.lockExpiry || null;
      const orderData = {
        quantity: locked.quantity,
        tnSelection: {
          lockRequestId: lockData.lockRequestId,
          ticketGroupId: locked.ticketGroupId,
          eventId,
          // unitPrice is our wholesale COST (fulfillment bids it to Mercury).
          // listUnitPrice is what the customer's item line must be priced at.
          ...(locked.wholesaleUnitPrice > 0 ? { unitPrice: locked.wholesaleUnitPrice } : {}),
          listUnitPrice: locked.listUnitPrice,
          allInUnitPrice: locked.allInUnitPrice,
          quantity: locked.quantity,
          ...(lockExpiresAt ? { lockExpiresAt: String(lockExpiresAt) } : {}),
          ...seatSel,
          ...deliveryMethodsFromLockData(lockData),
        },
      };
      const initialValues = {
        listing: listingForTnCheckout(listing, locked.listUnitPrice),
        orderData,
        confirmPaymentError: null,
      };
      if (typeof prepareCheckoutPage === 'function') {
        prepareCheckoutPage(initialValues, true);
      }
      trackSecondaryBeginCheckout({
        listing,
        orderData,
        dedupeKey: lockData.lockRequestId || `${listingId}:${ticketGroupId}`,
        // Ads/analytics revenue is the all-in price the customer actually pays.
        fallbackUnitPriceMajor: locked.allInUnitPrice,
      });
      trackOpenAIAdsCheckoutStarted({ listing: initialValues.listing, orderData });
      didNavigate = true;
      markSeaticsCheckoutNavigation();
      history.push(
        createResourceLocatorString(
          'CheckoutPage',
          routes,
          { id: listingId, slug: createSlug(listing?.attributes?.title || 'listing') },
          {}
        )
      );
    } catch (e) {
      trackSecondaryLockFailed({
        listing,
        reason: classifySecondaryLockFailureReason(e),
        status: e?.status,
        code: e?.data?.code || e?.code,
        ticketGroupId,
      });
      setSubmitError(checkoutAlertMessage(e));
      hidePageLoadIndicator();
    } finally {
      checkoutAttemptInFlightRef.current = false;
      setSubmitting(false);
      if (!didNavigate) {
        hidePageLoadIndicator();
      }
    }
  };

  return (
    <div className={css.seaticsPanel}>
      <h4 className={css.seaticsHeading}>Select Tickets</h4>
      <p className={css.seaticsRewardsHint}>
        Earn 5% cash back rewards on this purchase to use towards another event.
      </p>
      <div className={css.seaticsControls}>
        <label htmlFor="seatics-qty">Quantity</label>
        <select
          id="seatics-qty"
          value={quantity}
          onChange={e => setQuantity(Math.max(1, Number.parseInt(e.target.value, 10) || 1))}
          className={css.seaticsSelect}
        >
          {Array.from({ length: 8 }).map((_, i) => {
            const n = i + 1;
            return (
              <option key={n} value={n}>
                {n}
              </option>
            );
          })}
        </select>
      </div>

      <div className={css.seaticsControls}>
        <label htmlFor="seatics-section">Section</label>
        <select
          id="seatics-section"
          value={selectedSection}
          onChange={e => setSelectedSection(e.target.value)}
          className={css.seaticsSelect}
          disabled={!sections.length}
        >
          {sections.length ? null : <option value="">No sections available</option>}
          {sections.map(s => (
            <option key={s.section} value={s.section}>
              {s.section} ({s.groups} groups)
            </option>
          ))}
        </select>
      </div>

      <PrimaryButton onClick={onStartCheckout} disabled={!canSubmit} inProgress={submitting}>
        Continue to checkout
      </PrimaryButton>

      {loadError ? <p className={css.seaticsError}>{loadError}</p> : null}
      {!loadError &&
      availability &&
      availability.code !== 'bot_skipped' &&
      ticketGroups.length === 0 ? (
        <p className={css.seaticsHint}>{TN_EMPTY_INVENTORY_CUSTOMER_MESSAGE}</p>
      ) : null}
      {submitError ? <p className={css.seaticsError}>{submitError}</p> : null}
      <SeaticsRequoteModal
        id="SeaticsEventPurchasePanel.requote"
        modalState={requoteModal}
        onAccept={() => resolveRequoteDecision(true)}
        onDecline={() => resolveRequoteDecision(false)}
        onManageDisableScrolling={onManageDisableScrolling}
      />
    </div>
  );
};

SeaticsEventPurchasePanel.defaultProps = {
  currentUser: null,
  isOwnListing: false,
  onManageDisableScrolling: () => {},
  prepareCheckoutPage: null,
};

SeaticsEventPurchasePanel.propTypes = {
  listing: propTypes.listing.isRequired,
  currentUser: propTypes.currentUser,
  isOwnListing: bool,
  history: shape({ push: func.isRequired }).isRequired,
  routes: object.isRequired,
  onManageDisableScrolling: func,
  prepareCheckoutPage: func,
};

export const shouldRenderSeaticsPanel = listing =>
  process.env.REACT_APP_SEATICS_ENABLED === 'true' && isTnEventListing(listing);

export default SeaticsEventPurchasePanel;
